import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import EXIF from 'exif-js';
import Footer from './Footer';
import ShoppingCartPreview from './ShoppingCartPreview';
import BizOrderForm from './BizOrderForm';
import { trackAddToCart, trackBeginCheckout, trackFileUpload, trackPriceCalculation } from './GoogleAnalytics';
import { rematerializeFile, EXPIRED_FILE_MESSAGE } from './fileHealth';
import { MAX_IMAGES_PER_ORDER, LIMIT_REACHED_MESSAGE, cartOversizeMessage } from './orderLimits';

// REACT_APP_API_BASE_URL / REACT_APP_STRIPE_API_URL are baked in at build
// time by Vercel. Set them per environment (Production = 4534, Preview/
// Staging = 5d0c9). The localhost fallbacks are for local dev only — a
// missing env var in a Vercel build is intentionally loud instead of
// silently routing to a specific backend.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4001/api';
const STRIPE_API_URL = process.env.REACT_APP_STRIPE_API_URL || 'http://localhost:4001/api';

// Shown when the pricing list can't be loaded. Written for customers with no
// technical background: name what broke, give them something to try, and end
// with a human to email. No jargon — never "API", "server", or a status code.
//
// Two different messages because the customer can only act on one of them.
// UNREACHABLE = the request never completed, so the problem is between their
// device and us. A public school district blocked our pricing requests on
// 2026-08-13 and the old copy ("Failed to load product options") gave the
// customer nothing to go on, so the network hint is deliberately specific.
const PRICING_UNREACHABLE_MESSAGE =
  "We couldn't load pricing, so ordering is paused on this page.\n\n" +
  'First, try refreshing the page — this is usually temporary.\n\n' +
  "If that doesn't help, your internet connection may be blocking part of our " +
  'site. This is common on school, office, and guest Wi-Fi networks. Try again ' +
  "using your phone's data or a home connection.\n\n" +
  'Still stuck? Email us at support@presspeels.com.';

// SERVER_ERROR = we answered, just not correctly. Their network is fine, so
// telling them to switch Wi-Fi would send them chasing the wrong thing.
const PRICING_SERVER_ERROR_MESSAGE =
  "We couldn't load pricing, so ordering is paused on this page.\n\n" +
  "This one is on us, not your connection. Please refresh the page in a minute " +
  'to try again.\n\n' +
  'If it keeps happening, email us at support@presspeels.com.';

// Enhanced responsive breakpoints hook
const useResponsive = () => {
  const [screenSize, setScreenSize] = useState(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      return {
        width,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
        isSmallMobile: width < 376,  // iPhone SE, very small phones
        isStandardMobile: width >= 376 && width < 428,  // iPhone 12/13/14 (390px), iPhone 12/13 Pro (393px)
        isLargeMobile: width >= 428 && width < 768,  // iPhone 14 Pro Max (428px) and similar
        isSmallScreen: width < 1024,
        isLargeScreen: width >= 1200,
      };
    }
    return {
      width: 0,
      isMobile: false,
      isTablet: false,
      isDesktop: false,
      isSmallMobile: false,
      isStandardMobile: false,
      isLargeMobile: false,
      isSmallScreen: false,
      isLargeScreen: false,
    };
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        width,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
        isSmallMobile: width < 376,  // iPhone SE, very small phones
        isStandardMobile: width >= 376 && width < 428,  // iPhone 12/13/14 (390px), iPhone 12/13 Pro (393px)
        isLargeMobile: width >= 428 && width < 768,  // iPhone 14 Pro Max (428px) and similar
        isSmallScreen: width < 1024,
        isLargeScreen: width >= 1200,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return screenSize;
};

// Utility to parse variant size from title (e.g., '8x10', '2"x2"')
function parseVariantSize(title) {
  // Handles formats like 2"x2", 3"x3", 4x4, 4 x 4, 6"×4", etc.
  // Updated regex to handle × character as well as x/X
  const match = title.match(/(\d+(?:\.\d+)?)\s*"?\s*[xX×]\s*(\d+(?:\.\d+)?)\s*"?/);
  
  console.log('🔍 DEBUG: parseVariantSize called with:', title, 'result:', match);
  
  if (!match) return null;
  
  const result = { width: parseFloat(match[1]), height: parseFloat(match[2]) };
  console.log('🔍 DEBUG: parseVariantSize returning:', result);
  
  return result;
}



// Volume-based pricing function - rate based on TOTAL square inches (quantity included)
function calculateVolumePricing(widthInches, heightInches, quantity) {
  const perPrintSquareInches = widthInches * heightInches;
  const totalSquareInches = perPrintSquareInches * quantity;
  
  // Volume pricing tiers based on TOTAL square inches - Fixed tier boundaries to eliminate gaps
  let rate = 0;
  
  if (totalSquareInches >= 0 && totalSquareInches <= 10) {
    rate = 0.35;
  } else if (totalSquareInches > 10 && totalSquareInches <= 20) {
    rate = 0.25;
  } else if (totalSquareInches > 20 && totalSquareInches <= 50) {
    rate = 0.15;
  } else if (totalSquareInches > 50 && totalSquareInches <= 100) {
    rate = 0.1;
  } else if (totalSquareInches > 100 && totalSquareInches <= 200) {
    rate = 0.08;
  } else if (totalSquareInches > 200 && totalSquareInches <= 400) {
    rate = 0.07;
  } else if (totalSquareInches > 400 && totalSquareInches <= 600) {
    rate = 0.06;
  } else if (totalSquareInches > 600 && totalSquareInches <= 800) {
    rate = 0.05;
  } else if (totalSquareInches > 800 && totalSquareInches <= 1000) {
    rate = 0.04;
  } else if (totalSquareInches > 1000 && totalSquareInches <= 1500) {
    rate = 0.03;
  } else if (totalSquareInches > 1500 && totalSquareInches <= 2500) {
    rate = 0.025;
  } else if (totalSquareInches > 2500) {
    rate = 0.02;
  }
  
  const totalPrice = totalSquareInches * rate;
  
  return {
    perPrintSquareInches,
    totalSquareInches,
    rate,
    totalPrice,
    quantity
  };
}

// Dynamic weight calculation (matches backend)
function calculateDynamicWeight(squareInches) {
  return (squareInches * 0.00025) + 0.62;
}

// Find the closest variant by rounding up, allowing for image rotation
function findClosestVariant(widthIn, heightIn, variants) {
  console.log('🔍 DEBUG: findClosestVariant called with:', { widthIn, heightIn, variantsCount: variants.length });
  
  let bestFit = null;
  let minArea = Infinity;

  for (const variant of variants) {
    const variantSize = parseVariantSize(variant.title);
    if (!variantSize) continue;

    // Check if the image fits in the variant's dimensions, either normally or rotated
    const fitsAsIs = variantSize.width >= widthIn && variantSize.height >= heightIn;
    const fitsRotated = variantSize.width >= heightIn && variantSize.height >= widthIn;

    if (fitsAsIs || fitsRotated) {
      const variantArea = variantSize.width * variantSize.height;
      if (variantArea < minArea) {
        minArea = variantArea;
        bestFit = variant;
      }
    }
  }

  // If no variant is large enough, fallback to the largest available variant by area.
  if (!bestFit) {
    console.log('🔍 DEBUG: No perfect fit found, using largest variant');
    bestFit = variants.reduce((largestVariant, currentVariant) => {
      const currentSize = parseVariantSize(currentVariant.title);
      if (!currentSize) return largestVariant;
      const currentArea = currentSize.width * currentSize.height;

      if (!largestVariant) {
        return currentVariant;
      }
      
      const largestSize = parseVariantSize(largestVariant.title);
      if(!largestSize) return currentVariant;
      const largestArea = largestSize.width * largestSize.height;

      return currentArea > largestArea ? currentVariant : largestVariant;
    }, null);
  }

  console.log('🔍 DEBUG: findClosestVariant result:', bestFit ? bestFit.title : 'null');
  return bestFit;
}

// Image validation function
function validateImage(widthPx, heightPx, dpi, fileType) {
  const widthIn = widthPx / dpi;
  const heightIn = heightPx / dpi;
  
  if (!['image/png'].includes(fileType)) {
    return { valid: false, error: 'Only PNG files are accepted' };
  }
  if (dpi < 72) {
    return { valid: false, error: 'Image DPI must be 72 or higher for quality printing' };
  }
  if (dpi > 300) {
    return { valid: false, error: 'Image DPI must be 300 or lower for upload' };
  }
  if (widthIn < 1 || heightIn < 1) {
    return { valid: false, error: 'Image must be at least 1x1 inch' };
  }
  
  return { valid: true, widthIn, heightIn };
}

// Extract DPI from image file using multiple methods
function extractDPI(file) {
  return new Promise((resolve) => {
    // Method 1: Try EXIF first
    EXIF.getData(file, function() {
      const xResolution = EXIF.getTag(this, "XResolution");
      const yResolution = EXIF.getTag(this, "YResolution");
      const resolutionUnit = EXIF.getTag(this, "ResolutionUnit");
      
      console.log('EXIF Data:', { xResolution, yResolution, resolutionUnit });
      
      if (xResolution && yResolution) {
        let dpi = 300;
        if (resolutionUnit === 2) {
          // Resolution unit is inches
          dpi = Math.round(Number(xResolution));
        } else if (resolutionUnit === 3) {
          // Resolution unit is centimeters - convert to inches
          dpi = Math.round(Number(xResolution) * 2.54);
        } else if (resolutionUnit === 1 || !resolutionUnit) {
          // No unit specified - assume inches
          dpi = Math.round(Number(xResolution));
        }
        console.log('DPI from EXIF:', dpi, { xResolution, yResolution, resolutionUnit });
        resolve(dpi);
        return;
      }
      
      // Method 2: Try reading file as ArrayBuffer for PNG/other formats
      const reader = new FileReader();
      reader.onload = function(e) {
        const arrayBuffer = e.target.result;
        const uint8Array = new Uint8Array(arrayBuffer);
        
        // Check for PNG format
        if (uint8Array[0] === 0x89 && uint8Array[1] === 0x50 && uint8Array[2] === 0x4E && uint8Array[3] === 0x47) {
          // PNG file - look for pHYs chunk
          let offset = 8; // Skip PNG signature
          while (offset < uint8Array.length - 12) {
            const chunkLength = (uint8Array[offset] << 24) | (uint8Array[offset + 1] << 16) | (uint8Array[offset + 2] << 8) | uint8Array[offset + 3];
            const chunkType = String.fromCharCode(uint8Array[offset + 4], uint8Array[offset + 5], uint8Array[offset + 6], uint8Array[offset + 7]);
            
            if (chunkType === 'pHYs') {
              const ppuX = (uint8Array[offset + 8] << 24) | (uint8Array[offset + 9] << 16) | (uint8Array[offset + 10] << 8) | uint8Array[offset + 11];
              const ppuY = (uint8Array[offset + 12] << 24) | (uint8Array[offset + 13] << 16) | (uint8Array[offset + 14] << 8) | uint8Array[offset + 15];
              const unit = uint8Array[offset + 16];
              
              console.log('PNG pHYs data:', { ppuX, ppuY, unit });
              
              if (unit === 1) {
                // Pixels per meter - convert to DPI
                const dpi = Math.round(ppuX / 39.3701); // 1 meter = 39.3701 inches
                console.log('DPI from PNG:', dpi);
                resolve(dpi);
                return;
              }
            }
            offset += 12 + chunkLength; // Move to next chunk
          }
        }

        // Check for JPEG/JFIF format - read DPI from JFIF APP0 segment
        if (uint8Array[0] === 0xFF && uint8Array[1] === 0xD8) {
          let offset = 2;
          while (offset < uint8Array.length - 9) {
            if (uint8Array[offset] !== 0xFF) break;
            const marker = uint8Array[offset + 1];
            const segmentLength = (uint8Array[offset + 2] << 8) | uint8Array[offset + 3];
            if (marker === 0xE0) { // APP0
              // Check for JFIF identifier
              if (
                uint8Array[offset + 4] === 0x4A && uint8Array[offset + 5] === 0x46 &&
                uint8Array[offset + 6] === 0x49 && uint8Array[offset + 7] === 0x46 &&
                uint8Array[offset + 8] === 0x00
              ) {
                const units = uint8Array[offset + 11];
                const xDensity = (uint8Array[offset + 12] << 8) | uint8Array[offset + 13];
                if (units === 1 && xDensity > 0) {
                  console.log('DPI from JFIF APP0:', xDensity);
                  resolve(xDensity);
                  return;
                } else if (units === 2 && xDensity > 0) {
                  const dpi = Math.round(xDensity * 2.54);
                  console.log('DPI from JFIF APP0 (cm):', dpi);
                  resolve(dpi);
                  return;
                }
              }
            }
            if (marker === 0xD9 || marker === 0xDA) break; // EOI or SOS
            offset += 2 + segmentLength;
          }
        }

        // No DPI metadata found in file
        resolve(null);
      };
      reader.readAsArrayBuffer(file);
    });
  });
}

// IndexedDB helper functions for storing File objects
const openCartDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('PresspeelsCartDB', 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('cartFiles')) {
        const store = db.createObjectStore('cartFiles', { keyPath: 'id' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

const saveFileToIndexedDB = async (file, cartItemId) => {
  const db = await openCartDB();
  const transaction = db.transaction(['cartFiles'], 'readwrite');
  const store = transaction.objectStore('cartFiles');
  
  const now = Date.now();
  const fileData = {
    id: cartItemId,
    file: file,
    timestamp: now,
    expiresAt: now + (24 * 60 * 60 * 1000), // 24 hours from now
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type
  };
  
  return new Promise((resolve, reject) => {
    const request = store.put(fileData);
    request.onsuccess = () => resolve(cartItemId);
    request.onerror = () => reject(request.error);
  });
};

const getFileFromIndexedDB = async (cartItemId) => {
  const db = await openCartDB();
  const transaction = db.transaction(['cartFiles'], 'readwrite');
  const store = transaction.objectStore('cartFiles');
  
  return new Promise((resolve, reject) => {
    const request = store.get(cartItemId);
    request.onsuccess = () => {
      if (request.result) {
        // Check if file has expired
        if (request.result.expiresAt && Date.now() > request.result.expiresAt) {
          console.log('🗑️ Removing expired file from IndexedDB:', request.result.fileName);
          // Delete expired file
          const deleteRequest = store.delete(cartItemId);
          deleteRequest.onsuccess = () => resolve(null);
          deleteRequest.onerror = () => resolve(null);
        } else {
          resolve(request.result.file);
        }
      } else {
        resolve(null);
      }
    };
    request.onerror = () => reject(request.error);
  });
};

const clearCartFilesFromIndexedDB = async () => {
  const db = await openCartDB();
  const transaction = db.transaction(['cartFiles'], 'readwrite');
  const store = transaction.objectStore('cartFiles');
  
  return new Promise((resolve, reject) => {
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

const cleanupExpiredFilesFromIndexedDB = async () => {
  try {
    const db = await openCartDB();
    const transaction = db.transaction(['cartFiles'], 'readwrite');
    const store = transaction.objectStore('cartFiles');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const files = request.result;
        const now = Date.now();
        let expiredCount = 0;
        
        files.forEach(fileData => {
          if (fileData.expiresAt && now > fileData.expiresAt) {
            store.delete(fileData.id);
            expiredCount++;
          }
        });
        
        if (expiredCount > 0) {
          console.log(`🧹 Cleaned up ${expiredCount} expired files from IndexedDB`);
        }
        
        resolve(expiredCount);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error cleaning up expired files:', error);
  }
};

// Image Resizer Controls Component with Locked Aspect Ratio
const ImageResizerControls = ({ selectedFile, originalDimensions, currentPrintDimensions, onResizeComplete }) => {
  const [resizeState, setResizeState] = useState({
    widthInches: currentPrintDimensions.width,
    heightInches: currentPrintDimensions.height,
    dpi: 300,
    processing: false
  });

  // Calculate aspect ratio from current print dimensions
  const aspectRatio = currentPrintDimensions.width / currentPrintDimensions.height;

  const handleWidthChange = (newWidth) => {
    const numWidth = parseFloat(newWidth) || 0;
    setResizeState(prev => ({
      ...prev,
      widthInches: numWidth,
      heightInches: numWidth / aspectRatio // Auto-calculate height to maintain aspect ratio
    }));
  };

  const handleHeightChange = (newHeight) => {
    const numHeight = parseFloat(newHeight) || 0;
    setResizeState(prev => ({
      ...prev,
      heightInches: numHeight,
      widthInches: numHeight * aspectRatio // Auto-calculate width to maintain aspect ratio
    }));
  };

  const calculateOutputDimensions = () => {
    const widthPx = Math.round(resizeState.widthInches * resizeState.dpi);
    const heightPx = Math.round(resizeState.heightInches * resizeState.dpi);
    return { width: widthPx, height: heightPx };
  };

  const handleCreatePrintReady = async () => {
    if (!selectedFile || resizeState.widthInches <= 0 || resizeState.heightInches <= 0) {
      alert('Please ensure valid dimensions are entered');
      return;
    }

    setResizeState(prev => ({ ...prev, processing: true }));

    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('targetWidthInches', resizeState.widthInches.toString());
      formData.append('targetHeightInches', resizeState.heightInches.toString());
      formData.append('dpi', resizeState.dpi.toString());
      formData.append('format', 'png');

      const response = await fetch(`${API_BASE_URL}/resize-image`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        onResizeComplete(result);
      } else {
        throw new Error(result.error || 'Failed to create print-ready file');
      }
    } catch (error) {
      console.error('Resize error:', error);
      alert(`Failed to create print-ready file: ${error.message}`);
    } finally {
      setResizeState(prev => ({ ...prev, processing: false }));
    }
  };

  const outputDimensions = calculateOutputDimensions();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* CSS for custom slider styling */}
      <style>
        {`
          .resize-slider::-webkit-slider-thumb {
            appearance: none;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #ffffff;
            border: 2px solid #3b82f6;
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            transition: all 0.15s ease-in-out;
          }
          .resize-slider::-webkit-slider-thumb:hover {
            transform: scale(1.1);
            box-shadow: 0 4px 8px rgba(0,0,0,0.3);
          }
          .resize-slider.height-slider::-webkit-slider-thumb {
            border-color: #10b981;
          }
          .resize-slider::-moz-range-thumb {
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #ffffff;
            border: 2px solid #3b82f6;
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
          .resize-slider.height-slider::-moz-range-thumb {
            border-color: #10b981;
          }
        `}
      </style>
      {/* Current vs Target Dimensions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
        <div style={{ padding: '0.75rem', background: '#f0f9ff', borderRadius: '6px', border: '1px solid #0ea5e9' }}>
          <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#0369a1', marginBottom: '0.25rem' }}>
            Current Print Size
          </div>
          <div style={{ color: '#374151' }}>
            {currentPrintDimensions.width.toFixed(2)}" × {currentPrintDimensions.height.toFixed(2)}"
          </div>
        </div>
        <div style={{ padding: '0.75rem', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #10b981' }}>
          <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#065f46', marginBottom: '0.25rem' }}>
            Output Size
          </div>
          <div style={{ color: '#374151' }}>
            {outputDimensions.width}×{outputDimensions.height}px at {resizeState.dpi} DPI
          </div>
        </div>
      </div>

      {/* Resize Controls */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
              Width (inches) 🔒
            </label>
            <span style={{ fontSize: '0.75rem', color: '#3b82f6', fontWeight: '600', background: '#eff6ff', padding: '2px 6px', borderRadius: '4px' }}>
              {resizeState.widthInches.toFixed(2)}"
            </span>
          </div>
          <input
            type="number"
            step="0.01"
            min="0.1"
            max="100"
            value={resizeState.widthInches}
            onChange={(e) => handleWidthChange(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '1rem',
              marginBottom: '0.5rem'
            }}
            disabled={resizeState.processing}
          />
          <input
            type="range"
            className="resize-slider"
            min="0.1"
            max="100"
            step="0.1"
            value={resizeState.widthInches}
            onChange={(e) => handleWidthChange(e.target.value)}
            style={{
              width: '100%',
              height: '8px',
              borderRadius: '4px',
              background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(resizeState.widthInches / 100) * 100}%, #e5e7eb ${(resizeState.widthInches / 100) * 100}%, #e5e7eb 100%)`,
              outline: 'none',
              cursor: 'pointer',
              appearance: 'none',
              WebkitAppearance: 'none'
            }}
            disabled={resizeState.processing}
          />
        </div>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
              Height (inches) 🔒
            </label>
            <span style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: '600', background: '#ecfdf5', padding: '2px 6px', borderRadius: '4px' }}>
              {resizeState.heightInches.toFixed(2)}"
            </span>
          </div>
          <input
            type="number"
            step="0.01"
            min="0.1"
            max="100"
            value={resizeState.heightInches}
            onChange={(e) => handleHeightChange(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '1rem',
              marginBottom: '0.5rem'
            }}
            disabled={resizeState.processing}
          />
          <input
            type="range"
            className="resize-slider height-slider"
            min="0.1"
            max="100"
            step="0.1"
            value={resizeState.heightInches}
            onChange={(e) => handleHeightChange(e.target.value)}
            style={{
              width: '100%',
              height: '8px',
              borderRadius: '4px',
              background: `linear-gradient(to right, #10b981 0%, #10b981 ${(resizeState.heightInches / 100) * 100}%, #e5e7eb ${(resizeState.heightInches / 100) * 100}%, #e5e7eb 100%)`,
              outline: 'none',
              cursor: 'pointer',
              appearance: 'none',
              WebkitAppearance: 'none'
            }}
            disabled={resizeState.processing}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
            DPI Quality
          </label>
          <select
            value={resizeState.dpi}
            onChange={(e) => setResizeState(prev => ({ ...prev, dpi: parseInt(e.target.value) }))}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '1rem'
            }}
            disabled={resizeState.processing}
          >
            <option value={150}>150 DPI (Draft)</option>
            <option value={300}>300 DPI (Standard)</option>
            <option value={600}>600 DPI (High)</option>
          </select>
        </div>
      </div>

      {/* Aspect Ratio Info */}
      <div style={{ padding: '0.75rem', background: '#fffbeb', borderRadius: '6px', border: '1px solid #f59e0b' }}>
        <div style={{ fontSize: '0.875rem', color: '#92400e' }}>
          🔒 <strong>Aspect Ratio Locked:</strong> {aspectRatio.toFixed(3)} (adjusting one dimension automatically updates the other)
        </div>
      </div>

      {/* Create Button */}
      <button
        onClick={handleCreatePrintReady}
        disabled={resizeState.processing || resizeState.widthInches <= 0 || resizeState.heightInches <= 0}
        style={{
          padding: '0.75rem 1.5rem',
          background: resizeState.processing ? '#9ca3af' : '#059669',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          fontSize: '1rem',
          fontWeight: '600',
          cursor: resizeState.processing ? 'not-allowed' : 'pointer',
          transition: 'background-color 0.2s'
        }}
      >
        {resizeState.processing ? '🔄 Processing Image...' : '🖼️ Apply Resize to Cart'}
      </button>
    </div>
  );
};

// Individual Cart Item Resizer Component
const CartItemResizer = ({ cartItem, onItemResize, API_BASE_URL, setIsResizing }) => {
  const { isMobile } = useResponsive();
  // Check if item has already been resized from its customData
  const [enableResize, setEnableResize] = useState(false);
  const [hasBeenResized, setHasBeenResized] = useState(cartItem.customData?.isResized || false);
  const [resizeState, setResizeState] = useState({
    widthInches: cartItem.customData?.printWidth || 1,
    heightInches: cartItem.customData?.printHeight || 1,
    processing: false
  });

  // Sync state with cartItem prop changes (important for multiple items and after repricing)
  useEffect(() => {
    // Update hasBeenResized if the cart item's isResized flag changes
    if (cartItem.customData?.isResized && !hasBeenResized) {
      setHasBeenResized(true);
    }
    
    // Update resize state dimensions when cart item changes
    setResizeState(prev => ({
      ...prev,
      widthInches: cartItem.customData?.printWidth || prev.widthInches || 1,
      heightInches: cartItem.customData?.printHeight || prev.heightInches || 1
    }));
  }, [cartItem.customData?.printWidth, cartItem.customData?.printHeight, cartItem.customData?.isResized, hasBeenResized]);

  // Calculate aspect ratio from current cart item dimensions
  const aspectRatio = (cartItem.customData?.printWidth || 1) / (cartItem.customData?.printHeight || 1);

  const handleWidthChange = (newWidth) => {
    const numWidth = parseFloat(newWidth);
    if (isNaN(numWidth) || numWidth <= 0) return; // Don't update if invalid
    
    const clampedWidth = Math.min(Math.max(numWidth, 0.5), 16); // Clamp between 0.5 and 16 inches
    const newHeight = clampedWidth / aspectRatio;
    
    setResizeState(prev => ({
      ...prev,
      widthInches: clampedWidth,
      heightInches: Math.max(newHeight, 0.5) // Ensure height is at least 0.5
    }));
  };

  const handleHeightChange = (newHeight) => {
    const numHeight = parseFloat(newHeight);
    if (isNaN(numHeight) || numHeight <= 0) return; // Don't update if invalid
    
    const correspondingWidth = numHeight * aspectRatio;
    const clampedWidth = Math.min(Math.max(correspondingWidth, 0.5), 16); // Clamp between 0.5 and 16"
    const recalculatedHeight = clampedWidth / aspectRatio;
    
    setResizeState(prev => ({
      ...prev,
      heightInches: Math.max(recalculatedHeight, 0.5), // Ensure height is at least 0.5
      widthInches: clampedWidth
    }));
  };

  const handleResize = async () => {
    // Validate dimensions before proceeding
    const width = parseFloat(resizeState.widthInches);
    const height = parseFloat(resizeState.heightInches);
    
    if (!cartItem.file || isNaN(width) || isNaN(height) || width <= 0 || height <= 0) {
      alert('Cannot resize this item - missing file or invalid dimensions');
      return;
    }

    setResizeState(prev => ({ ...prev, processing: true }));
    if (setIsResizing) setIsResizing(true); // Notify parent component

    try {
      // Prefer the server-side copy of the image (saved at upload time) over
      // re-uploading bytes — the large upload is what fails on phones. Resize
      // is only offered before an item has been resized, so item.file here is
      // still the full-res original and safe to fall back to.
      const sourcePath = cartItem.customData?.processedImageTempPath || null;

      const buildForm = async (withBytes) => {
        const formData = new FormData();
        if (withBytes) {
          // Read into memory first — a dead blob (iOS evicts saved file data)
          // would truncate the upload mid-send and fail server-side.
          const freshFile = await rematerializeFile(cartItem.file);
          if (!freshFile) return null;
          formData.append('image', freshFile);
        }
        formData.append('fileName', cartItem.fileName);
        if (sourcePath) formData.append('sourceImageTempPath', sourcePath);
        formData.append('targetWidthInches', resizeState.widthInches.toString());
        formData.append('targetHeightInches', resizeState.heightInches.toString());
        formData.append('dpi', '300');
        formData.append('format', 'png');
        // Pass the old spot TIFF path so the server can delete it after generating a new one
        if (cartItem.customData?.spotTifTempPath) {
          formData.append('oldSpotTifTempPath', cartItem.customData.spotTifTempPath);
        }
        return formData;
      };

      let formData = await buildForm(!sourcePath);
      if (!formData) {
        alert(EXPIRED_FILE_MESSAGE(cartItem.fileName));
        return;
      }
      let response = await fetch(`${API_BASE_URL}/resize-image`, {
        method: 'POST',
        body: formData
      });

      // Server copy expired (redeploy) or old backend — retry with the bytes
      if (!response.ok && sourcePath) {
        let code = null;
        let errText = '';
        try {
          const d = await response.clone().json();
          code = d.code;
          errText = d.error || '';
        } catch (e) { /* non-JSON error body */ }
        if ((response.status === 409 && code === 'TEMP_FILE_MISSING') ||
            (response.status === 400 && errText.includes('No image file provided'))) {
          console.log('🔁 Server copy unavailable — retrying resize with direct upload');
          formData = await buildForm(true);
          if (!formData) {
            alert(EXPIRED_FILE_MESSAGE(cartItem.fileName));
            return;
          }
          response = await fetch(`${API_BASE_URL}/resize-image`, {
            method: 'POST',
            body: formData
          });
        }
      }

      // Check if response is ok first
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Server response not ok:', response.status, errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      // Check content type before parsing JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const responseText = await response.text();
        console.error('❌ Response is not JSON:');
        console.error('❌ Content-Type:', contentType);
        console.error('❌ Response text (first 500 chars):', responseText.substring(0, 500));
        console.error('❌ Response status:', response.status);
        console.error('❌ Response headers:', Object.fromEntries(response.headers.entries()));
        throw new Error(`Server returned non-JSON response (${response.status}): ${contentType}`);
      }

      const result = await response.json();

      if (result.success) {
        // Validate dimensions before passing to onItemResize
        const width = parseFloat(resizeState.widthInches);
        const height = parseFloat(resizeState.heightInches);
        
        if (isNaN(width) || isNaN(height) || width <= 0 || height <= 0) {
          throw new Error('Invalid dimensions after resize');
        }
        
        // Update the cart item with new resized data (lightweight)
        onItemResize(cartItem.cartItemId, {
          imageDataUrl: result.imageDataUrl, // preview only
          printWidth: width,
          printHeight: height,
          finalDimensions: result.finalDimensions,
          resizedImageTempPath: result.resizedImageTempPath, // token for backend
          spotTifTempPath: result.spotTifTempPath || null,   // freshly-generated TIFF at resize dimensions
          isResized: true
        });
        setHasBeenResized(true); // Lock the checkbox after successful resize
      } else {
        throw new Error(result.error || 'Failed to resize image');
      }
    } catch (error) {
      console.error('❌ Resize error details:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error stack:', error.stack);
      alert(`Failed to resize image: ${error.message}`);
    } finally {
      setResizeState(prev => ({ ...prev, processing: false }));
      if (setIsResizing) setIsResizing(false); // Reset parent state
    }
  };

  return (
    <div style={{
      marginTop: '1rem',
      paddingTop: '1rem',
      borderTop: '1px solid #e2e8f0',
      position: 'relative'
    }}>
      {/* CSS for custom slider styling */}
      <style>
        {`
          .cart-resize-slider::-webkit-slider-thumb {
            appearance: none;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #ffffff;
            border: 2px solid #3b82f6;
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            transition: all 0.15s ease-in-out;
          }
          .cart-resize-slider::-webkit-slider-thumb:hover {
            transform: scale(1.1);
            box-shadow: 0 4px 8px rgba(0,0,0,0.3);
          }
          .cart-resize-slider.height-slider::-webkit-slider-thumb {
            border-color: #10b981;
          }
          @keyframes pulse-warning {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
        `}
      </style>
      
      {/* Processing Overlay Warning */}
      {resizeState.processing && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(239, 68, 68, 0.95)',
          borderRadius: '6px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: isMobile ? '1rem' : '2rem',
          overflowY: 'auto',
          animation: 'pulse-warning 2s ease-in-out infinite'
        }}>
          <div style={{
            fontSize: '3rem',
            marginBottom: '1rem'
          }}>⚠️</div>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: 'white',
            marginBottom: '0.5rem',
            textAlign: 'center'
          }}>
            DO NOT REFRESH!
          </div>
          <div style={{
            fontSize: '1rem',
            color: 'white',
            textAlign: 'center',
            marginBottom: '1rem'
          }}>
            Resizing image in progress...
          </div>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid rgba(255, 255, 255, 0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      )}

      {/* Resize toggle button */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button
          onClick={() => !hasBeenResized && setEnableResize(v => !v)}
          disabled={hasBeenResized}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            padding: '0.625rem 1rem',
            minWidth: 'min(240px, 100%)',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: hasBeenResized ? '#059669' : (enableResize ? '#b45309' : '#92400e'),
            background: hasBeenResized
              ? 'linear-gradient(135deg, #f0fdf4 0%, #bbf7d0 100%)'
              : enableResize
                ? 'linear-gradient(135deg, #fffbeb 0%, #fde68a 100%)'
                : 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
            border: hasBeenResized
              ? '1px solid #6ee7b7'
              : enableResize
                ? '1px solid #f59e0b'
                : '1px solid #fcd34d',
            borderRadius: '8px',
            cursor: hasBeenResized ? 'not-allowed' : 'pointer',
            opacity: 1,
            transition: 'all 0.2s ease'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
          </svg>
          {hasBeenResized ? 'Image Resized' : 'Resize Image'}
        </button>
        <span style={{
          fontSize: '0.75rem',
          color: hasBeenResized ? '#059669' : '#6b7280',
          fontStyle: 'italic'
        }}>
          {hasBeenResized ? '✅ Image has been resized' : '(This will alter your original image)'}
        </span>
      </div>

      {/* Info - only visible when resize is enabled */}
      {(hasBeenResized || enableResize) && (
        <div style={{
          padding: '0.75rem',
          background: '#fffbeb',
          border: '1px solid #f59e0b',
          borderRadius: '4px',
          marginBottom: '1rem',
          fontSize: '0.875rem',
          color: '#92400e'
        }}>
          📏 <strong>Max width: 16 inches</strong> • 🔒 <strong>Aspect ratio locked</strong> • Height adjusts automatically
        </div>
      )}

      {/* Resize Controls - only visible when resize is enabled */}
      {(hasBeenResized || enableResize) && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '0.5rem' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
              Width (inches) 🔒
            </label>
            <span style={{ 
              fontSize: '0.75rem', 
              color: '#3b82f6', 
              fontWeight: '600', 
              background: '#eff6ff', 
              padding: '2px 6px', 
              borderRadius: '4px' 
            }}>
              {(resizeState.widthInches || 0).toFixed(2)}"
            </span>
          </div>
          <input
            type="number"
            step="0.01"
            min="0.1"
            max="16"
            value={resizeState.widthInches || ''}
            onChange={(e) => handleWidthChange(e.target.value)}
            onBlur={(e) => {
              const val = parseFloat(e.target.value);
              if (isNaN(val) || val < 0.1) handleWidthChange('0.1');
            }}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '0.875rem',
              marginBottom: '0.5rem',
              background: 'white',
              color: '#374151'
            }}
            disabled={resizeState.processing}
          />
          <input
            type="range"
            className="cart-resize-slider"
            min="0.1"
            max="16"
            step="0.1"
            value={Math.max(0.1, resizeState.widthInches || 0.1)}
            onChange={(e) => handleWidthChange(e.target.value)}
            style={{
              width: '100%',
              height: '6px',
              borderRadius: '3px',
              background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(Math.max(0.1, resizeState.widthInches || 0.1) / 16) * 100}%, #e5e7eb ${(Math.max(0.1, resizeState.widthInches || 0.1) / 16) * 100}%, #e5e7eb 100%)`,
              outline: 'none',
              cursor: 'pointer',
              appearance: 'none',
              WebkitAppearance: 'none'
            }}
            disabled={resizeState.processing}
          />
        </div>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '0.5rem' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
              Height (inches) 🔒
            </label>
            <span style={{
              fontSize: '0.75rem',
              color: '#10b981',
              fontWeight: '600',
              background: '#ecfdf5',
              padding: '2px 6px',
              borderRadius: '4px'
            }}>
              {(resizeState.heightInches || 0).toFixed(2)}"
            </span>
          </div>
          <input
            type="number"
            step="0.01"
            min="0.1"
            max="100"
            value={resizeState.heightInches || ''}
            onChange={(e) => handleHeightChange(e.target.value)}
            onBlur={(e) => {
              const val = parseFloat(e.target.value);
              if (isNaN(val) || val < 0.1) handleHeightChange('0.1');
            }}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '0.875rem',
              marginBottom: '0.5rem',
              background: 'white',
              color: '#374151'
            }}
            disabled={resizeState.processing}
          />
          <input
            type="range"
            className="cart-resize-slider height-slider"
            min="0.1"
            max={Math.min(100, 16 / aspectRatio)} // Dynamic max based on 16" width limit
            step="0.1"
            value={Math.max(0.1, resizeState.heightInches || 0.1)}
            onChange={(e) => handleHeightChange(e.target.value)}
            style={{
              width: '100%',
              height: '6px',
              borderRadius: '3px',
              background: `linear-gradient(to right, #10b981 0%, #10b981 ${(Math.max(0.1, resizeState.heightInches || 0.1) / Math.min(100, 16 / aspectRatio)) * 100}%, #e5e7eb ${(Math.max(0.1, resizeState.heightInches || 0.1) / Math.min(100, 16 / aspectRatio)) * 100}%, #e5e7eb 100%)`,
              outline: 'none',
              cursor: 'pointer',
              appearance: 'none',
              WebkitAppearance: 'none'
            }}
            disabled={resizeState.processing}
          />
        </div>
        </div>
      )}

      {/* Action Button - only visible when resize is enabled */}
      {(hasBeenResized || enableResize) && (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={handleResize}
            disabled={resizeState.processing}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: resizeState.processing ? '#9ca3af' : '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: resizeState.processing ? 'not-allowed' : 'pointer'
            }}
          >
            {resizeState.processing ? '🔄 Resizing...' : hasBeenResized ? '🔄 Re-apply Resize' : '✅ Apply Resize'}
          </button>
        </div>
      )}
    </div>
  );
};

export default function ProductPage({ isBiz = false }) {
  // File input ref to clear it when form is reset
  const fileInputRef = useRef(null);
  
  // Enhanced responsive state
  const { isMobile, isTablet, isDesktop, isSmallMobile, isStandardMobile, isLargeMobile, isSmallScreen } = useResponsive();
  
  // State management
  const [selectedFile, setSelectedFile] = useState(null);
  const [lastUploadedFile, setLastUploadedFile] = useState(null); // Keep reference for resizing
  const [imgWidth, setImgWidth] = useState(null);
  const [imgHeight, setImgHeight] = useState(null);
  const [printWidth, setPrintWidth] = useState(null);
  const [printHeight, setPrintHeight] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [quantityInput, setQuantityInput] = useState('1');
  const [variants, setVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [cartItems, setCartItems] = useState([]);
  const [loadingCart, setLoadingCart] = useState(false);
  const checkoutInProgress = useRef(false); // synchronous guard — prevents double-submit before React re-renders
  const cartInitializedRef = useRef(false); // prevents save effect from wiping localStorage before restore completes
  const [showBizForm, setShowBizForm] = useState(false);

  const [showDPIInput, setShowDPIInput] = useState(false);
  const [manualDPI, setManualDPI] = useState(150);
  const [detectedDPI, setDetectedDPI] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  
  // Cart drag and drop state
  const [isCartDragging, setIsCartDragging] = useState(false);
  
  // Cart state
  const [cartRestored, setCartRestored] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  
  // Load cart items from localStorage and IndexedDB on component mount
  useEffect(() => {
    const restoreCart = async () => {
      const savedCartData = localStorage.getItem('presspeels_cart_metadata');
      if (savedCartData) {
        try {
          const parsedData = JSON.parse(savedCartData);
          
          // Handle both old format (array) and new format (object with timestamp)
          let cartMetadata, isExpired = false;
          
          if (Array.isArray(parsedData)) {
            // Old format without expiration - treat as expired to migrate to new format
            console.log('🔄 Migrating old cart format to new format with expiration');
            cartMetadata = parsedData;
            isExpired = true;
          } else if (parsedData.items && parsedData.expiresAt) {
            // New format with expiration
            cartMetadata = parsedData.items;
            isExpired = Date.now() > parsedData.expiresAt;
            
            if (isExpired) {
              console.log('⏰ Cart data expired, clearing stale cart');
            }
          } else {
            console.warn('Invalid cart data format');
            isExpired = true;
          }
          
          if (isExpired) {
            // Clear expired cart data
            localStorage.removeItem('presspeels_cart_metadata');
            await clearCartFilesFromIndexedDB();
            return;
          }
          
          if (Array.isArray(cartMetadata) && cartMetadata.length > 0) {
            console.log('🛒 Restoring cart from localStorage and IndexedDB:', cartMetadata.length, 'items');
            
            // Restore files from IndexedDB and combine with metadata
            const restoredCartItems = await Promise.all(
              cartMetadata.map(async (metadata) => {
                try {
                  const storedFile = await getFileFromIndexedDB(metadata.cartItemId);
                  // Verify the blob is actually readable and copy it into
                  // memory — iOS Safari can evict IndexedDB blob data, leaving
                  // a File that reports a size but truncates when uploaded.
                  const file = await rematerializeFile(storedFile);
                  if (file) {
                    return {
                      ...metadata,
                      file: file,
                      imagePreview: URL.createObjectURL(file)
                    };
                  } else {
                    console.warn('File missing or unreadable in IndexedDB for item:', metadata.fileName);
                    return null;
                  }
                } catch (error) {
                  console.error('Error retrieving file from IndexedDB:', error);
                  return null;
                }
              })
            );
            
            // Filter out items where file restoration failed
            const validCartItems = restoredCartItems.filter(item => item !== null);
            
            if (validCartItems.length > 0) {
              setCartItems(validCartItems);
              setCartRestored(true);
              console.log('✅ Successfully restored', validCartItems.length, 'cart items with files');
              
              // Hide the restoration notice after 5 seconds
              setTimeout(() => {
                setCartRestored(false);
              }, 5000);
            }
          }
        } catch (error) {
          console.error('Error parsing saved cart metadata:', error);
          // Clear invalid data
          localStorage.removeItem('presspeels_cart_metadata');
          await clearCartFilesFromIndexedDB();
        }
      }
    };

    restoreCart().finally(() => {
      cartInitializedRef.current = true;
    });

    // Clean up expired files from IndexedDB
    cleanupExpiredFilesFromIndexedDB();

    // Check if user returned from checkout
    const checkoutInitiated = localStorage.getItem('presspeels_checkout_initiated');
    const checkoutTimestamp = localStorage.getItem('presspeels_checkout_timestamp');
    
    if (checkoutInitiated && checkoutTimestamp) {
      const timeSinceCheckout = Date.now() - parseInt(checkoutTimestamp);
      // If more than 24 hours have passed, clear the checkout flags
      // This prevents stale checkout states
      if (timeSinceCheckout > 24 * 60 * 60 * 1000) {
        localStorage.removeItem('presspeels_checkout_initiated');
        localStorage.removeItem('presspeels_checkout_timestamp');
      }
    }
  }, []);

  // Prevent page refresh/navigation during resize (critical operation)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Critical warning: Resize in progress - this CANNOT be recovered
      if (isResizing) {
        e.preventDefault();
        e.returnValue = '⚠️ IMAGE RESIZE IN PROGRESS! You will lose your resize progress if you leave now!';
        return e.returnValue;
      }
      
      // Note: No warning for cart items since they persist via IndexedDB
      // Cart will be automatically restored when user returns (within 30 minutes)
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isResizing]);

  // Save cart items to localStorage whenever cartItems changes
  useEffect(() => {
    // Don't wipe localStorage before the initial restore has finished
    if (!cartInitializedRef.current) return;

    if (cartItems.length > 0) {
      // Store cart metadata with timestamp for expiration
      const cartData = {
        items: cartItems.map(item => ({
          cartItemId: item.cartItemId, // Key for IndexedDB lookup
          fileName: item.fileName,
          variant: item.variant,
          variantId: item.variantId,
          printSize: item.printSize,
          dimensions: item.dimensions,
          price: item.price,
          quantity: item.quantity,
          customData: item.customData,
          // Store file info for verification
          fileInfo: {
            name: item.fileName,
            size: item.file?.size,
            type: item.file?.type,
            lastModified: item.file?.lastModified
          }
        })),
        timestamp: Date.now(),
        expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours from now
      };
      localStorage.setItem('presspeels_cart_metadata', JSON.stringify(cartData));
      console.log('💾 Cart saved to localStorage (expires in 24 hours)');
    } else {
      // Clear localStorage when cart is empty
      localStorage.removeItem('presspeels_cart_metadata');
    }
  }, [cartItems]);

  // Wipe the saved cart once a business order is placed. Stripe orders get
  // this from SuccessPage, but the business form redirects to /biz-success,
  // which never touched storage — so the placed order came back on the next
  // visit, inviting a duplicate. State is left alone: the page is about to
  // navigate away, and emptying cartItems first would flash an empty cart.
  const clearSavedCart = async () => {
    localStorage.removeItem('presspeels_cart_metadata');
    try {
      await clearCartFilesFromIndexedDB();
    } catch (error) {
      // Without the metadata the files can't be restored anyway, and they
      // expire on their own; never hold up the success page over this.
      console.error('Error clearing cart files after business order:', error);
    }
  };

  // Function to clear cart (after successful checkout)
  const clearCart = async () => {
    try {
      await clearCartFilesFromIndexedDB();
      setCartItems([]);
      localStorage.removeItem('presspeels_cart_metadata');
      console.log('🧹 Cart and files cleared successfully');
    } catch (error) {
      console.error('Error clearing cart files:', error);
      // Still clear the cart items even if IndexedDB cleanup fails
      setCartItems([]);
      localStorage.removeItem('presspeels_cart_metadata');
    }
  };
  
      // Fetch variants from API
  useEffect(() => {
    const fetchVariants = async () => {
      setLoadingVariants(true);
      try {
        console.log('🔍 DEBUG: Fetching variants from:', `${API_BASE_URL}/products`);
        const response = await fetch(`${API_BASE_URL}/products`);

        // We got an answer, it was just a bad one — that's ours to fix, and it
        // was previously swallowed with no message shown to the customer at all.
        if (!response.ok) {
          console.error('Variants request failed with status:', response.status);
          setError(PRICING_SERVER_ERROR_MESSAGE);
          return;
        }

        const data = await response.json();
        console.log('🔍 DEBUG: Received variants data:', data);

        // Find the "Custom Sheet Print" product
        const customSheetProduct = data.products?.find(p => p.node.title === 'Custom Sheet Print');

        if (!customSheetProduct) {
          console.error('Could not find "Custom Sheet Print" product in the product list:', data.products);
          setError(PRICING_SERVER_ERROR_MESSAGE);
          return;
        }

        const productVariants = customSheetProduct.node.variants.edges.map(edge => edge.node);
        console.log('🔍 DEBUG: Setting variants:', productVariants.length, 'variants loaded');
        console.log('🔍 DEBUG: First few variants:', productVariants.slice(0, 3));
        setVariants(productVariants);
      } catch (err) {
        // fetch() only rejects when the request never completed — offline, DNS
        // failure, or a network filter dropping it. A filter's own block page
        // also lands here, since that HTML fails to parse as JSON.
        console.error('Error fetching variants:', err);
        setError(PRICING_UNREACHABLE_MESSAGE);
      } finally {
        setLoadingVariants(false);
      }
    };

    fetchVariants();
  }, []);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Every way of adding an image — the file picker, the page drop zone and
      // the cart's "Add New Image" tile — arrives here, so the cap is enforced
      // once, at the door. Past it the upload still processes and lands in the
      // cart, only to be thrown out at checkout: Stripe rejects the session on
      // retail, multer rejects the upload on business. Both flows, one limit.
      if (cartItems.length >= MAX_IMAGES_PER_ORDER) {
        setError(LIMIT_REACHED_MESSAGE);
        setSelectedFile(null);
        // Without this the same file can't be picked again after removing a
        // cart item — the input still holds it, so onChange never fires.
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Check file type first
      if (!['image/png'].includes(file.type)) {
        setError('Only PNG files are accepted. Please upload a PNG image.');
        setSelectedFile(null);
        return;
      }
      
      setError('');
      setSuccessMessage(''); // Clear success message
      setSelectedVariant(null);
      setPrintWidth(null);
      setPrintHeight(null);

      // Reject files with no embedded DPI metadata
      const fileDpi = await extractDPI(file);
      if (fileDpi === null) {
        setError(
          'This file does not contain DPI metadata. Open the file in your design software, set a resolution (e.g. 300 DPI), re-export as PNG, and upload again.'
        );
        setSelectedFile(null);
        return;
      }
      
      try {
        // Auto-process image: ONLY trim transparent edges on upload
        // White removal is now an OPTIONAL button the user can click
        console.log('✂️ Auto-trimming transparent edges...');
        const formData = new FormData();
        formData.append('image', file);
        formData.append('removeWhite', 'false'); // Only trim, don't remove white automatically
        
        const trimResponse = await fetch(`${API_BASE_URL}/process-image`, {
          method: 'POST',
          body: formData
        });
        
        let processedFile = file; // Fallback to original file if processing fails
        
        let serverDpi = null; // DPI from server response
        let serverSpotTifTempPath = null; // Spot TIFF temp path from server
        let serverProcessedImageTempPath = null; // Full-res processed PNG saved server-side
        
        if (trimResponse.ok) {
          const trimResult = await trimResponse.json();
          if (trimResult.success && trimResult.imageDataUrl) {
            // Convert base64 data URL back to File object
            const base64Data = trimResult.imageDataUrl.split(',')[1];
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'image/png' });
            processedFile = new File([blob], file.name.replace(/\.(jpg|jpeg)$/i, '.png'), { type: 'image/png' });
            
            // Get DPI from server response (preserved from original image)
            serverDpi = trimResult.dpi || 300;
            serverSpotTifTempPath = trimResult.spotTifTempPath || null;
            serverProcessedImageTempPath = trimResult.processedImageTempPath || null;
            
            console.log(`✅ Image processed: ${trimResult.originalDimensions.width}x${trimResult.originalDimensions.height} → ${trimResult.finalDimensions.width}x${trimResult.finalDimensions.height}${trimResult.whiteRemoved ? ' (white removed)' : ''} @ ${serverDpi} DPI`);
            if (serverSpotTifTempPath) {
              console.log(`🎨 Spot TIFF generated: ${serverSpotTifTempPath}`);
            }
          }
        } else {
          console.log('⚠️ Auto-processing failed, using original image');
        }
        
        setSelectedFile(processedFile);
        setLastUploadedFile(processedFile); // Keep reference for resizing
        
        // Use DPI from server if available, otherwise extract from original file
        let dpi;
        if (serverDpi) {
          dpi = serverDpi;
          console.log('Using DPI from server:', dpi);
        } else {
          dpi = await extractDPI(file); // Extract from ORIGINAL file, not processed
          console.log('Extracted DPI from original:', dpi);
        }
        
        // If DPI couldn't be detected reliably, show manual input
        // For PNG files, we can usually detect DPI from pHYs chunk
        // For other formats, we may need manual input
        if (!file.type.includes('png') && !file.type.includes('jpeg') && !file.type.includes('jpg') && dpi === 300) {
          setShowDPIInput(true);
          return;
        }
        
        const img = new Image();
        img.onload = () => {
          const width = img.width;
          const height = img.height;
          setImgWidth(width);
          setImgHeight(height);
          
          // Use manual DPI if user is in manual input mode, otherwise use detected DPI
          const finalDPI = showDPIInput ? manualDPI : dpi;
          
          // Store the detected DPI for display
          setDetectedDPI(finalDPI);
          
          // Calculate print size from metadata
          const widthIn = width / finalDPI;
          const heightIn = height / finalDPI;
          setPrintWidth(widthIn);
          setPrintHeight(heightIn);
          
          console.log('🔍 DEBUG: Image processed:', { width, height, finalDPI, widthIn, heightIn });
          
          // Track file upload in Google Analytics
          trackFileUpload(processedFile.name, processedFile.size);
          
          // Validate image with extracted DPI and file type
          const validation = validateImage(width, height, finalDPI, processedFile.type);
          console.log('🔍 DEBUG: Image validation result:', validation);
          
          if (!validation.valid) {
            setError(validation.error);
            setSelectedFile(null);
            setImgWidth(null);
            setImgHeight(null);
            setPrintWidth(null);
            setPrintHeight(null);
            return;
          }
          
          // If valid, find closest variant
          console.log('🔍 DEBUG: Calling findClosestVariant with:', validation.widthIn, validation.heightIn, 'variants count:', variants.length);
          
          // EMERGENCY FIX: Add timeout to ensure variants are loaded
          const attemptFindVariant = () => {
            if (variants.length === 0) {
              console.log('🔍 DEBUG: Variants not loaded yet, retrying in 500ms');
              setTimeout(attemptFindVariant, 500);
              return;
            }
            
            const closest = findClosestVariant(validation.widthIn, validation.heightIn, variants);
            console.log('🔍 DEBUG: Setting selectedVariant to:', closest);
            
            if (!closest) {
              console.log('🔍 DEBUG: No variant found, forcing 6x4 variant as fallback');
              // Force a fallback variant for common sizes
              const fallbackVariant = variants.find(v => v.title.includes('6"×4"')) || variants[0];
              console.log('🔍 DEBUG: Using fallback variant:', fallbackVariant);
              setSelectedVariant(fallbackVariant);
              
              // Automatically add to cart with fallback variant
              setTimeout(() => {
                addToCartFromUpload(processedFile, fallbackVariant, width, height, finalDPI, validation.widthIn, validation.heightIn, serverSpotTifTempPath, serverProcessedImageTempPath);
              }, 100);
            } else {
              setSelectedVariant(closest);

              // Automatically add to cart with closest variant
              setTimeout(() => {
                addToCartFromUpload(processedFile, closest, width, height, finalDPI, validation.widthIn, validation.heightIn, serverSpotTifTempPath, serverProcessedImageTempPath);
              }, 100);
            }
          };
          
          attemptFindVariant();
        };
        img.src = URL.createObjectURL(processedFile);
      } catch (err) {
        console.error('Error extracting DPI:', err);
        setError('Unable to read image metadata. Please ensure your image is 72 DPI or higher.');
        setSelectedFile(null);
      }
    }
  };

  const handleManualDPIChange = (event) => {
    const dpi = parseInt(event.target.value);
    setManualDPI(dpi);
    setDetectedDPI(dpi); // Update displayed DPI
    
    if (imgWidth && imgHeight && selectedFile) {
      // Recalculate print size with new DPI
      const widthIn = imgWidth / dpi;
      const heightIn = imgHeight / dpi;
      setPrintWidth(widthIn);
      setPrintHeight(heightIn);
      
      const validation = validateImage(imgWidth, imgHeight, dpi, selectedFile.type);
      if (!validation.valid) {
        setError(validation.error);
        setSelectedVariant(null);
      } else {
        setError('');
        const closest = findClosestVariant(validation.widthIn, validation.heightIn, variants);
        setSelectedVariant(closest);
      }
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragging to false if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file only');
        return;
      }
      
      // Validate file size (100MB limit)
      if (file.size > 100 * 1024 * 1024) {
        setError('File size must be less than 100MB');
        return;
      }
      
      // Create a fake event object for handleFileChange
      const fakeEvent = {
        target: {
          files: [file]
        }
      };
      handleFileChange(fakeEvent);
    }
  };

  // Cart drag and drop handlers (for "Add New Image" button area)
  const handleCartDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCartDragging(true);
  };

  const handleCartDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isCartDragging to false if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsCartDragging(false);
    }
  };

  const handleCartDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleCartDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCartDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      
      // Validate file type - only PNG
      if (!['image/png'].includes(file.type)) {
        setError('Only PNG files are accepted. Please upload a PNG image.');
        return;
      }
      
      // Validate file size (100MB limit)
      if (file.size > 100 * 1024 * 1024) {
        setError('File size must be less than 100MB');
        return;
      }
      
      // Create a fake event object for handleFileChange
      const fakeEvent = {
        target: {
          files: [file]
        }
      };
      handleFileChange(fakeEvent);
    }
  };

  const cartContainerRef = useRef(null);
  const shouldScrollToCartRef = useRef(false);

  React.useEffect(() => {
    if (shouldScrollToCartRef.current && cartItems.length > 0) {
      // Allow DOM to paint updated cart before scrolling
      requestAnimationFrame(() => {
        // Prefer scrolling to the specific last-added item if present
        const lastAdded = cartItems[cartItems.length - 1];
        if (lastAdded && lastAdded.cartItemId) {
          const el = document.getElementById(`cart-item-${lastAdded.cartItemId}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            shouldScrollToCartRef.current = false;
            return;
          }
        }
        // Fallback: scroll to cart container
        if (cartContainerRef.current) {
          cartContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        shouldScrollToCartRef.current = false;
      });
    }
  }, [cartItems]);

  // Auto add to cart function when image is uploaded
  const addToCartFromUpload = async (file, variant, width, height, dpi, printWidthIn, printHeightIn, spotTifTempPath = null, processedImageTempPath = null) => {
    try {
      const variantSize = parseVariantSize(variant.title);
      const cartItemId = `cart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Save file to IndexedDB for cart persistence
      await saveFileToIndexedDB(file, cartItemId);
      
      const cartItem = {
        cartItemId: cartItemId,
        file: file,
        fileName: file.name,
        imagePreview: URL.createObjectURL(file),
        variant: variant,
        variantId: `dtf_${variantSize.width}x${variantSize.height}`,
        printSize: variant.title,
        dimensions: `${width} × ${height} pixels`,
        price: calculateVolumePricing(printWidthIn, printHeightIn, quantity).totalPrice * 100, // Convert to cents
        quantity: quantity,
        customData: {
          imgWidth: width,
          imgHeight: height,
          printWidth: printWidthIn,
          printHeight: printHeightIn,
          variantId: variant.id,
          dpi: dpi,
          totalSquareInches: calculateVolumePricing(printWidthIn, printHeightIn, quantity).totalSquareInches,
          whiteRemoved: false,
          spotTifTempPath: spotTifTempPath,
          // Server-side copy of the processed full-res PNG. Checkout sends
          // this reference instead of re-uploading the image from the device.
          processedImageTempPath: processedImageTempPath
        }
      };

      setCartItems(prev => {
        shouldScrollToCartRef.current = true;
        const newCart = [...prev, cartItem];
        return repriceCartItems(newCart); // Reprice all items based on new total volume
      });
      
      // Track add to cart in Google Analytics
      trackAddToCart('USD', cartItem.price / 100, [{
        id: cartItem.variantId,
        name: `Custom Print - ${cartItem.printSize}`,
        category: 'Custom Prints',
        quantity: cartItem.quantity,
        price: cartItem.price / 100 / cartItem.quantity, // Price per unit
      }]);
      
      // Reset form for next upload
      setSelectedFile(null);
      setImgWidth(null);
      setImgHeight(null);
      setSelectedVariant(null);
      setQuantity(1);
      setQuantityInput('1');
      setError('');
      
      // Clear file input element to allow same file to be uploaded again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Reset drag states
      setIsDragging(false);
      setDragCounter(0);
      
      // Clear DPI input state
      setShowDPIInput(false);
      setManualDPI(150);
      setDetectedDPI(null);
      
      console.log('✅ Item automatically added to cart:', cartItem);
      setSuccessMessage(`✅ ${file.name} added to cart! Upload another image or proceed to checkout.`);
      
    } catch (error) {
      console.error('Error auto-adding to cart:', error);
      setError('Failed to add item to cart automatically. Please try again.');
    }
  };

  // Function to reprice all cart items based on total volume
  const repriceCartItems = (items) => {
    if (items.length === 0) return items;
    
    // Calculate total square inches across all items with validation
    const totalSquareInches = items.reduce((total, item) => {
      const width = parseFloat(item.customData?.printWidth);
      const height = parseFloat(item.customData?.printHeight);
      const quantity = parseInt(item.quantity, 10) || 0;
      
      // Only include valid dimensions (positive numbers)
      if (!isNaN(width) && !isNaN(height) && width > 0 && height > 0 && quantity > 0) {
        return total + (width * height * quantity);
      }
      return total;
    }, 0);
    
    // Determine volume pricing rate based on total - Fixed tier boundaries to eliminate gaps
    let volumeRate = 0;
    if (totalSquareInches >= 0 && totalSquareInches <= 10) {
      volumeRate = 0.35;
    } else if (totalSquareInches > 10 && totalSquareInches <= 20) {
      volumeRate = 0.25;
    } else if (totalSquareInches > 20 && totalSquareInches <= 50) {
      volumeRate = 0.15;
    } else if (totalSquareInches > 50 && totalSquareInches <= 100) {
      volumeRate = 0.1;
    } else if (totalSquareInches > 100 && totalSquareInches <= 200) {
      volumeRate = 0.08;
    } else if (totalSquareInches > 200 && totalSquareInches <= 400) {
      volumeRate = 0.07;
    } else if (totalSquareInches > 400 && totalSquareInches <= 600) {
      volumeRate = 0.06;
    } else if (totalSquareInches > 600 && totalSquareInches <= 800) {
      volumeRate = 0.05;
    } else if (totalSquareInches > 800 && totalSquareInches <= 1000) {
      volumeRate = 0.04;
    } else if (totalSquareInches > 1000 && totalSquareInches <= 1500) {
      volumeRate = 0.03;
    } else if (totalSquareInches > 1500 && totalSquareInches <= 2500) {
      volumeRate = 0.025;
    } else if (totalSquareInches > 2500) {
      volumeRate = 0.02;
    }
    
    // Only calculate prices if we have valid total square inches
    if (totalSquareInches <= 0 || volumeRate <= 0) {
      // If no valid dimensions, return items with existing prices (don't zero them out)
      return items.map(item => ({
        ...item,
        price: item.price || 0
      }));
    }
    
    // Calculate exact total price in cents (to match order total display)
    const exactTotalPriceCents = Math.round(totalSquareInches * volumeRate * 100);

    // Price each valid item independently at the aggregate volume rate.
    // itemSquareInches * volumeRate is exactly the item's share of the total, so
    // the per-item prices already sum to the exact total (within sub-cent
    // rounding). Computing it per-item — instead of the old
    // proportion-of-exactTotal then "dump the leftover on items[0]" approach —
    // means an item that momentarily has invalid dimensions can no longer poison
    // the total and force the first item's price negative.
    const validIndexes = [];
    const repricedItems = items.map((item, idx) => {
      const width = parseFloat(item.customData?.printWidth);
      const height = parseFloat(item.customData?.printHeight);
      const quantity = parseInt(item.quantity, 10) || 0;

      // Only calculate price if dimensions are valid
      if (!isNaN(width) && !isNaN(height) && width > 0 && height > 0 && quantity > 0) {
        const itemSquareInches = width * height * quantity;
        validIndexes.push(idx);
        return { ...item, price: Math.round(itemSquareInches * volumeRate * 100) };
      }
      // Dimensions invalid — keep existing price, and leave this item out of the
      // rounding reconciliation below so it can never be pushed negative.
      return { ...item, price: item.price || 0 };
    });

    // Reconcile only the tiny sub-cent rounding residual (a few cents at most),
    // and only across freshly-priced valid items. Applied to the first valid
    // item and floored at zero so a price can never go negative.
    if (validIndexes.length > 0) {
      const firstValid = validIndexes[0];
      const validTotal = validIndexes.reduce((sum, i) => sum + repricedItems[i].price, 0);
      const residual = exactTotalPriceCents - validTotal;
      repricedItems[firstValid] = {
        ...repricedItems[firstValid],
        price: Math.max(0, repricedItems[firstValid].price + residual)
      };
    }

    return repricedItems;
  };

  // Cart management functions
  const addToCart = async () => {
    if (!selectedFile || !selectedVariant || !printWidth || !printHeight) {
      setError('Please upload an image and wait for processing to complete');
      return;
    }

    try {
      const variantSize = parseVariantSize(selectedVariant.title);
      const cartItemId = `cart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Save file to IndexedDB for cart persistence
      await saveFileToIndexedDB(selectedFile, cartItemId);
      
      const cartItem = {
        cartItemId: cartItemId,
        file: selectedFile,
        fileName: selectedFile.name,
        imagePreview: URL.createObjectURL(selectedFile),
        variant: selectedVariant,
        variantId: `dtf_${variantSize.width}x${variantSize.height}`,
        printSize: selectedVariant.title,
        dimensions: `${imgWidth} × ${imgHeight} pixels`,
        price: calculateVolumePricing(printWidth, printHeight, quantity).totalPrice * 100, // Convert to cents (will be repriced)
        quantity: quantity,
        customData: {
          imgWidth: imgWidth,
          imgHeight: imgHeight,
          printWidth: printWidth,  // Use actual calculated print dimensions
          printHeight: printHeight, // Use actual calculated print dimensions
          variantId: selectedVariant.id
        }
      };

      setCartItems(prev => {
        shouldScrollToCartRef.current = true;
        const newCart = [...prev, cartItem];
        return repriceCartItems(newCart); // Reprice all items based on new total volume
      });
      
      // Track add to cart in Google Analytics
      trackAddToCart('USD', cartItem.price / 100, [{
        id: cartItem.variantId,
        name: `Custom Print - ${cartItem.printSize}`,
        category: 'Custom Prints',
        quantity: cartItem.quantity,
        price: cartItem.price / 100 / cartItem.quantity, // Price per unit
      }]);
      
      // Reset form for next upload
      setSelectedFile(null);
      setImgWidth(null);
      setImgHeight(null);
      setSelectedVariant(null);
      setQuantity(1);
      setQuantityInput('1');
      setError('');
      
      // Clear file input element to allow same file to be uploaded again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Reset drag states
      setIsDragging(false);
      setDragCounter(0);
      
      // Clear print dimensions
      setPrintWidth(null);
      setPrintHeight(null);
      
      // Clear DPI input state
      setShowDPIInput(false);
      setManualDPI(150);
      setDetectedDPI(null);
      
      // Show success message
              setSuccessMessage('✅ Added to cart! Upload another image or scroll down to checkout.');
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      
    } catch (error) {
      console.error('Error adding item to cart:', error);
      setError('Failed to add item to cart. Please try again.');
    }
  };

  const updateCartItem = (index, newQuantity) => {
    setCartItems(prev => {
      const updatedCart = prev.map((item, i) => {
        if (i === index) {
          return { 
            ...item, 
            quantity: newQuantity
          };
        }
        return item;
      });
      return repriceCartItems(updatedCart); // Reprice all items based on new total volume
    });
  };

  const removeCartItem = (index) => {
    setCartItems(prev => {
      const filteredCart = prev.filter((_, i) => i !== index);
      return repriceCartItems(filteredCart); // Reprice remaining items based on new total volume
    });
  };

  // Handle individual cart item resizing
  const handleCartItemResize = async (cartItemId, resizeData) => {
    // Convert resized image data URL to File object for storage
    let resizedFile = null;
    if (resizeData.imageDataUrl) {
      try {
        // Convert base64 data URL to Blob
        const base64Data = resizeData.imageDataUrl.split(',')[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/png' });
        
        // Find the original filename from the cart item
        const originalItem = cartItems.find(item => item.cartItemId === cartItemId);
        const fileName = originalItem ? originalItem.fileName : 'resized_image.png';
        
        // Create a new File object from the blob
        resizedFile = new File([blob], fileName, { type: 'image/png' });
        
        console.log('✅ Converted resized image to File object:', {
          fileName: resizedFile.name,
          size: Math.round(resizedFile.size / 1024) + 'KB',
          type: resizedFile.type
        });
      } catch (err) {
        console.error('❌ Failed to convert resized image to File:', err);
      }
    }

    setCartItems(prev => {
      const updatedCart = prev.map(item => {
        if (item.cartItemId === cartItemId) {
          // Update the cart item with resized data
          const updatedItem = {
            ...item,
            file: resizedFile || item.file, // ✅ Update with resized file, fallback to original
            imagePreview: resizeData.imageDataUrl, // Update preview image
            customData: {
              ...item.customData,
              printWidth: resizeData.printWidth,
              printHeight: resizeData.printHeight,
              // Use lightweight token instead of large base64
              resizedImageTempPath: resizeData.resizedImageTempPath,
              isResized: true,
              // Replace the spot TIFF with the one freshly generated from the resized pixels.
              // Without this, the old TIFF (at pre-resize dimensions) would be sent to the printer.
              ...(resizeData.spotTifTempPath && { spotTifTempPath: resizeData.spotTifTempPath })
            }
          };
          
          // Validate dimensions before calculating price
          const width = parseFloat(resizeData.printWidth);
          const height = parseFloat(resizeData.printHeight);
          const quantity = parseInt(item.quantity, 10) || 0;
          
          // Only calculate price if dimensions are valid
          if (!isNaN(width) && !isNaN(height) && width > 0 && height > 0 && quantity > 0) {
            const volumePricing = calculateVolumePricing(width, height, quantity);
            updatedItem.price = volumePricing.totalPrice * 100; // Convert to cents
          } else {
            // Keep existing price if dimensions are invalid
            console.warn('Invalid dimensions after resize:', { width, height, quantity });
            updatedItem.price = item.price || 0;
          }
          
          return updatedItem;
        }
        return item;
      });
      
      // Reprice all items based on new total volume
      return repriceCartItems(updatedCart);
    });
    
    // Save the resized file to IndexedDB for cart persistence
    if (resizedFile) {
      try {
        await saveFileToIndexedDB(resizedFile, cartItemId);
        console.log('✅ Saved resized file to IndexedDB');
      } catch (err) {
        console.error('❌ Failed to save resized file to IndexedDB:', err);
      }
    }
    
    // Show success message
    setSuccessMessage(`✅ Image resized to ${resizeData.printWidth.toFixed(2)}" × ${resizeData.printHeight.toFixed(2)}" and cart updated!`);
  };

  // Handle remove white background for a cart item (called from ShoppingCartPreview button)
  const handleRemoveWhiteBackground = async (cartItemId, result) => {
    try {
      // Convert the processed image data URL to a File object
      const base64Data = result.imageDataUrl.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });
      
      // Find the original cart item
      const originalItem = cartItems.find(item => item.cartItemId === cartItemId);
      if (!originalItem) {
        console.error('Cart item not found:', cartItemId);
        return;
      }
      
      const processedFile = new File([blob], originalItem.fileName, { type: 'image/png' });
      
      // Update the cart item with the processed image
      setCartItems(prevItems => {
        const updatedCart = prevItems.map(item => {
          if (item.cartItemId === cartItemId) {
            // Calculate new dimensions based on DPI
            const dpi = result.dpi || 72;
            const newWidthIn = result.finalDimensions.width / dpi;
            const newHeightIn = result.finalDimensions.height / dpi;
            
            // Find matching variant
            const newVariant = findClosestVariant(newWidthIn, newHeightIn, variants);
            
            return {
              ...item,
              file: processedFile,
              imagePreview: result.imageDataUrl,
              variant: newVariant || item.variant,
              customData: {
                ...item.customData,
                whiteRemoved: true,
                printWidth: newWidthIn,
                printHeight: newHeightIn,
                pixelWidth: result.finalDimensions.width,
                pixelHeight: result.finalDimensions.height,
                spotTifTempPath: result.spotTifTempPath || null,
                // The reprocessed image is a new server-side file; the old
                // reference no longer matches the current pixels
                processedImageTempPath: result.processedImageTempPath || null
              }
            };
          }
          return item;
        });
        
        return repriceCartItems(updatedCart);
      });
      
      // Save updated file to IndexedDB
      await saveFileToIndexedDB(processedFile, cartItemId);
      
      setSuccessMessage('✅ White removed and image trimmed!');
    } catch (err) {
      console.error('Failed to update cart item after white removal:', err);
    }
  };

  const handleRemoveBlackBackground = async (cartItemId, result) => {
    try {
      const base64Data = result.imageDataUrl.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });

      const originalItem = cartItems.find(item => item.cartItemId === cartItemId);
      if (!originalItem) {
        console.error('Cart item not found:', cartItemId);
        return;
      }

      const processedFile = new File([blob], originalItem.fileName, { type: 'image/png' });

      setCartItems(prevItems => {
        const updatedCart = prevItems.map(item => {
          if (item.cartItemId === cartItemId) {
            const dpi = result.dpi || 72;
            const newWidthIn = result.finalDimensions.width / dpi;
            const newHeightIn = result.finalDimensions.height / dpi;

            const newVariant = findClosestVariant(newWidthIn, newHeightIn, variants);

            return {
              ...item,
              file: processedFile,
              imagePreview: result.imageDataUrl,
              variant: newVariant || item.variant,
              customData: {
                ...item.customData,
                blackRemoved: true,
                printWidth: newWidthIn,
                printHeight: newHeightIn,
                pixelWidth: result.finalDimensions.width,
                pixelHeight: result.finalDimensions.height,
                spotTifTempPath: result.spotTifTempPath || null,
                processedImageTempPath: result.processedImageTempPath || null
              }
            };
          }
          return item;
        });

        return repriceCartItems(updatedCart);
      });

      await saveFileToIndexedDB(processedFile, cartItemId);

      setSuccessMessage('✅ Black removed and image trimmed!');
    } catch (err) {
      console.error('Failed to update cart item after black removal:', err);
    }
  };

  // Server-side file reference for an item, if one exists. Resized items must
  // use the resized full-res file (item.file is only a 600px preview for them).
  const serverFileReference = (item) => {
    const cd = item.customData || {};
    if (cd.isResized) return cd.resizedImageTempPath || null;
    return cd.processedImageTempPath || null;
  };

  // forceUploadIds: cartItemIds that must upload their actual file bytes even
  // though a server reference exists — used by the automatic retry when the
  // server reports its temp copy is gone (e.g. backend redeploy).
  const handleMultiItemCheckout = async (items, forceUploadIds = null) => {
    if (items.length === 0) return;

    // A cart restored from IndexedDB can be over the cap even though the cart
    // UI now refuses to add past it. Stripe would reject the session for a
    // reason the customer can do nothing with ("Failed to create checkout
    // session"), so refuse it here, in words that name the fix.
    if (items.length > MAX_IMAGES_PER_ORDER) {
      setError(cartOversizeMessage(items.length));
      return;
    }

    const originalItems = items;
    setLoadingCart(true);
    try {
      // Decide per item: reference the server-side copy (tiny request — the
      // reliable path on mobile) or upload the file bytes. Files that will be
      // uploaded are read into memory first: a dead blob (iOS can evict saved
      // file data) would otherwise truncate the multipart upload mid-send.
      const preparedItems = [];
      for (const item of items) {
        const reference = serverFileReference(item);
        const mustUpload = !reference || (forceUploadIds && forceUploadIds.has(item.cartItemId));
        if (!mustUpload) {
          preparedItems.push({ ...item, uploadFile: false });
          continue;
        }
        const freshFile = await rematerializeFile(item.file);
        if (!freshFile) {
          setError(EXPIRED_FILE_MESSAGE(item.fileName));
          return;
        }
        preparedItems.push({ ...item, file: freshFile, uploadFile: true });
      }
      items = preparedItems;
      const uploadCount = items.filter(i => i.uploadFile).length;
      console.log(`📤 Checkout: ${items.length - uploadCount} item(s) by server reference, ${uploadCount} uploading file bytes`);

      // Debug: Log the URL being used
      console.log('🔗 Connecting to:', `${STRIPE_API_URL}/stripe-checkout`);
      console.log('🌐 NODE_ENV:', process.env.NODE_ENV);
      console.log('🔧 STRIPE_API_URL:', STRIPE_API_URL);
      
      // Debug: Log what items we're sending
      console.log('📦 Cart items being sent:', items.map(item => ({
        fileName: item.fileName,
        quantity: item.quantity,
        price: item.price, // This should be total price including quantity
        pricePerUnit: item.price / item.quantity,
        customData: item.customData
      })));
      
      const firstItem = items[0];
      
      // Track begin checkout in Google Analytics
      const totalValue = items.reduce((sum, item) => sum + item.price, 0) / 100; // Convert from cents
      const gaItems = items.map(item => ({
        id: item.variantId,
        name: `Custom Print - ${item.printSize}`,
        category: 'Custom Prints',
        quantity: item.quantity,
        price: item.price / 100 / item.quantity, // Price per unit
      }));
      trackBeginCheckout('USD', totalValue, gaItems);
      
      console.log('📋 First item data:', {
        fileName: firstItem.fileName,
        quantity: firstItem.quantity,
        price: firstItem.price,
        pricePerUnit: firstItem.price / firstItem.quantity,
        variantId: firstItem.variantId,
        customData: firstItem.customData
      });
      
      const formData = new FormData();
      if (firstItem.uploadFile) {
        formData.append('image', firstItem.file);
      }
      // Always send the filename: in reference mode there is no uploaded file
      // to carry it, and the server needs it for the Stripe line item.
      formData.append('fileName', firstItem.fileName);
      formData.append('quantity', firstItem.quantity.toString());
      formData.append('customData', JSON.stringify(firstItem.customData));
      formData.append('variantId', firstItem.variantId);
      
      // Add pricing information for backend
      formData.append('totalPrice', firstItem.price.toString()); // Total price in cents
      formData.append('pricePerUnit', Math.round(firstItem.price / firstItem.quantity).toString()); // Per unit price in cents
      
      // Add all additional files and their metadata
      if (items.length > 1) {
        const additionalItems = [];
        
        for (let i = 1; i < items.length; i++) {
          const item = items[i];

          // Upload the file only when there is no server-side reference
          if (item.uploadFile) {
            formData.append(`additionalFile_${i}`, item.file);
          }
          
          // Collect metadata for this additional item
          additionalItems.push({
            fileName: item.fileName,
            printSize: item.printSize,
            quantity: item.quantity,
            price: item.price, // Total price including quantity (in cents)
            pricePerUnit: Math.round(item.price / item.quantity), // Per-unit price in cents
            variantId: item.variantId,
            customData: item.customData,
            fileIndex: i // Reference to the file field name
          });
        }
        
        // Send metadata for all additional items
        formData.append('additionalItems', JSON.stringify(additionalItems));
        formData.append('additionalItemCount', (items.length - 1).toString());
        
        console.log(`📦 Sending ${items.length} items total (1 primary + ${items.length - 1} additional)`);
      }

      const response = await fetch(`${STRIPE_API_URL}/stripe-checkout`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (data.success && data.url) {
        // Store a flag to indicate checkout was initiated
        // This helps distinguish between back navigation and page refresh
        localStorage.setItem('presspeels_checkout_initiated', 'true');
        localStorage.setItem('presspeels_checkout_timestamp', Date.now().toString());

        window.location.href = data.url;
      } else if (data.code === 'TEMP_FILE_MISSING' && !forceUploadIds) {
        // The server lost its copy of some images (e.g. backend redeploy).
        // Retry once, uploading the actual bytes for just those items. Resized
        // items can't be recovered this way — locally we only hold their 600px
        // preview, never print-quality pixels.
        const missing = Array.isArray(data.missingItems) ? data.missingItems : [];
        const retryIds = new Set();
        const unrecoverable = [];
        for (const m of missing) {
          const item = originalItems[m.fileIndex];
          if (!item) continue;
          if (item.customData?.isResized) {
            unrecoverable.push(item.fileName);
          } else {
            retryIds.add(item.cartItemId);
          }
        }
        if (unrecoverable.length > 0) {
          setError(
            `The resized image${unrecoverable.length > 1 ? 's' : ''} ` +
            `"${unrecoverable.join('", "')}" expired on our server. Please remove ` +
            `${unrecoverable.length > 1 ? 'them' : 'it'} from the cart, upload the original again, and redo the resize.`
          );
          return;
        }
        console.log(`🔁 Server temp files missing for ${retryIds.size} item(s) — retrying checkout with direct upload`);
        await handleMultiItemCheckout(originalItems, retryIds);
      } else if (!forceUploadIds && response.status === 400 && data.error === 'Primary image file required') {
        // Backend doesn't support reference checkout yet (not redeployed) —
        // retry the old way, uploading every file.
        console.log('🔁 Backend lacks reference-checkout support — retrying with full upload');
        await handleMultiItemCheckout(originalItems, new Set(originalItems.map(i => i.cartItemId)));
      } else {
        setError(data.error || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Failed to create checkout session');
    } finally {
      setLoadingCart(false);
    }
  };

  const handleStripeCheckout = async () => {
    if (!selectedVariant || !selectedFile || !printWidth || !printHeight) {
      setError('Please upload an image and wait for processing to complete');
      return;
    }

    setLoadingCart(true);
    setError('');

    try {
      // Convert variant to Stripe format
      const variantSize = parseVariantSize(selectedVariant.title);
      const stripeVariantId = `dtf_${variantSize.width}x${variantSize.height}`;
      
      console.log('Converting variant:', {
        variantId: selectedVariant.id,
        variantTitle: selectedVariant.title,
        stripeId: stripeVariantId
      });

      // Calculate pricing for this item
      const pricing = calculateVolumePricing(printWidth, printHeight, quantity);
      const totalPriceCents = pricing.totalPrice * 100;
      const pricePerUnitCents = Math.round(totalPriceCents / quantity);
      
      // Track begin checkout in Google Analytics
      trackBeginCheckout('USD', pricing.totalPrice, [{
        id: stripeVariantId,
        name: `Custom Print - ${selectedVariant.title}`,
        category: 'Custom Prints',
        quantity: quantity,
        price: pricing.totalPrice / quantity, // Price per unit
      }]);
      
      console.log('📋 Single item checkout data:', {
        quantity: quantity,
        totalPrice: totalPriceCents,
        pricePerUnit: pricePerUnitCents,
        printWidth: printWidth,
        printHeight: printHeight,
        variantId: stripeVariantId
      });

      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('quantity', quantity.toString());
      formData.append('customData', JSON.stringify({
        imgWidth: imgWidth,
        imgHeight: imgHeight,
        printWidth: printWidth,  // Use actual calculated print dimensions
        printHeight: printHeight, // Use actual calculated print dimensions
        variantId: selectedVariant.id
      }));
      formData.append('variantId', stripeVariantId);
      
      // Add pricing information for backend
      formData.append('totalPrice', totalPriceCents.toString()); // Total price in cents
      formData.append('pricePerUnit', pricePerUnitCents.toString()); // Per unit price in cents

      const response = await fetch(`${STRIPE_API_URL}/stripe-checkout`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (data.success && data.url) {
        // Store a flag to indicate checkout was initiated
        localStorage.setItem('presspeels_checkout_initiated', 'true');
        localStorage.setItem('presspeels_checkout_timestamp', Date.now().toString());
        
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to create Stripe checkout session');
      }
    } catch (err) {
      console.error('Stripe checkout error:', err);
      setError('Failed to create Stripe checkout session');
    } finally {
      setLoadingCart(false);
    }
  };

  if (isBiz && showBizForm) {
    return <BizOrderForm cartItems={cartItems} onBack={() => setShowBizForm(false)} onOrderPlaced={clearSavedCart} />;
  }

  const mainCheckoutQty = cartItems.reduce((total, item) => total + item.quantity, 0);
  const mainCheckoutLabel = isBiz
    ? (cartItems.length > 0
        ? `Proceed to Order Form (${mainCheckoutQty} print${mainCheckoutQty !== 1 ? 's' : ''})`
        : 'Upload Images to Continue')
    : (loadingCart
        ? 'Processing...'
        : cartItems.length > 0
          ? `Checkout Cart (${mainCheckoutQty} print${mainCheckoutQty !== 1 ? 's' : ''})`
          : (printWidth && printHeight ? 'Buy Now' : 'Upload Image for Pricing'));

  return (
    <div style={{
      minHeight: '100vh',
      background: 'transparent',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <header style={{
        background: 'white',
        textAlign: 'center',
        padding: isSmallMobile ? '0.75rem 0.5rem' : isStandardMobile ? '1rem 0.75rem' : isMobile ? '1.125rem 0.75rem' : isTablet ? '1.25rem 1rem' : '1.375rem 0.5rem',
        boxShadow: '0 2px 20px rgba(0, 0, 0, 0.1)'
      }}>
        <img 
          src="/press-peels-logo-02.svg" 
          alt="Press Peels" 
          style={{ 
            maxWidth: '100%',
            height: 'auto',
            width: isSmallMobile ? '200px' : isStandardMobile ? '280px' : isMobile ? '320px' : isTablet ? '350px' : '476px',
            maxHeight: isSmallMobile ? '76px' : isStandardMobile ? '106px' : isMobile ? '122px' : isTablet ? '118px' : '136px',
            objectFit: 'contain'
          }} 
        />
      </header>

      {/* Cart Restored Notification */}
      {cartRestored && (
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          padding: isSmallMobile ? '0.875rem 1rem' : '1rem 1.5rem',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
          borderBottom: '2px solid #047857',
          animation: 'slideDown 0.3s ease-out'
        }}>
          <style>
            {`
              @keyframes slideDown {
                from { transform: translateY(-100%); opacity: 0; }
                to { transform: translateY(0); opacity: 1; }
              }
            `}
          </style>
          <div style={{
            fontSize: isSmallMobile ? '1rem' : '1.125rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}>
            <span>✅</span>
            <span>Cart Restored Successfully!</span>
          </div>
          <div style={{
            fontSize: isSmallMobile ? '0.75rem' : '0.85rem',
            fontWeight: '400',
            opacity: 0.95,
            marginTop: '0.25rem'
          }}>
            Your previous cart with {cartItems.length} item{cartItems.length !== 1 ? 's' : ''} has been recovered.
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{
        flex: 1,
        padding: isSmallMobile ? '1rem 0.5rem' : isStandardMobile ? '1.25rem 0.75rem' : isMobile ? '1.5rem 1rem' : isTablet ? '2.5rem 1.5rem' : '4rem 2rem',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        background: '#ffffff',
        borderRadius: isSmallMobile ? 12 : isStandardMobile ? 14 : isMobile ? 16 : isTablet ? 20 : 24,
        boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)',
        padding: isSmallMobile ? '1.5rem' : isStandardMobile ? '1.75rem' : isMobile ? '2rem' : isTablet ? '3rem' : '4rem',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        
        {/* Header Section - Two Column Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr',
          gap: isSmallMobile ? '2rem' : isStandardMobile ? '2.25rem' : isMobile ? '2.5rem' : isTablet ? '3.5rem' : '5rem',
          marginBottom: isSmallMobile ? '1rem' : isStandardMobile ? '1.125rem' : isMobile ? '1.25rem' : isTablet ? '1.75rem' : '2.5rem',
          alignItems: 'start'
        }}>
          {/* Left Side - Upload Your Artwork */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            textAlign: 'left',
            gap: '1rem'
          }}>
            <h2 style={{ 
              margin: 0, 
              fontSize: isSmallMobile ? '1.25rem' : isStandardMobile ? '1.35rem' : isMobile ? '1.4rem' : isTablet ? '1.6rem' : '1.8rem', 
              fontWeight: '600', 
              color: '#333',
              letterSpacing: '-0.025em',
              lineHeight: '1.3'
            }}>
              Upload Your Artwork
            </h2>
            <p style={{ 
              fontSize: isSmallMobile ? '0.875rem' : isStandardMobile ? '0.9rem' : isMobile ? '0.95rem' : '1.1rem', 
              color: '#666666', 
              lineHeight: '1.6',
              margin: 0
            }}>
              Upload your image and we'll calculate the perfect print size and pricing.
            </p>
          </div>

          {/* Right Side - Custom Press Peels */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            textAlign: 'left',
            gap: '0.5rem'
          }}>
            <div style={{
              margin: 0, 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start'
            }}>
              <img 
                src="/press-peels-logo-trimmed.svg" 
                alt="Custom Press Peels" 
                style={{
                  height: isMobile ? '1.5rem' : '2rem',
                  width: 'auto',
                  display: 'block'
                }}
              />
            </div>
            <p style={{ 
              fontSize: isSmallMobile ? '0.95rem' : isStandardMobile ? '0.975rem' : isMobile ? '1rem' : '1.1rem', 
              color: '#6b7280', 
              lineHeight: '1.6',
              margin: 0
            }}>
              {isBiz
                ? 'High-quality Custom DTF Transfers'
                : 'At Press Peels, we make high-quality DTF transfers affordable and easy, so you can spend less time ordering and more time creating.'}
            </p>
          </div>
        </div>

        {/* Main Content Layout - Upload Box and 4 Boxes Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr',
          gap: isSmallMobile ? '2rem' : isStandardMobile ? '2.25rem' : isMobile ? '2.5rem' : isTablet ? '3.5rem' : '5rem',
          alignItems: 'start'
        }}>
        
        {/* Left Column - Image Upload & Preview */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: isSmallMobile ? '1.5rem' : isStandardMobile ? '1.75rem' : '2rem' }}>
          {/* Upload Area */}
          <div 
            style={{
              border: isDragging ? '3px dashed #10b981' : '3px dashed #d1d5db',
              borderRadius: isSmallMobile ? '12px' : '16px',
              padding: isSmallMobile ? '2rem 1.5rem' : isStandardMobile ? '2.25rem 1.5rem' : isMobile ? '2.5rem 2rem' : isTablet ? '3rem 2rem' : '3.5rem 2rem',
              textAlign: 'center',
              background: isDragging ? '#f0fdf4' : '#fafafa',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              transform: isDragging && !isSmallMobile ? 'scale(1.02)' : 'scale(1)',
              boxShadow: isDragging ? '0 8px 32px rgba(16, 185, 129, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.05)',
              minHeight: isSmallMobile ? '280px' : isStandardMobile ? '295px' : isMobile ? '310px' : '330px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <label htmlFor="image-upload" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%', gap: '0.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                {isDragging ? (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    transform: 'scale(1.1)',
                    transition: 'transform 0.2s ease'
                  }}>
                    <img 
                      src="/press-peels-logo-03.svg" 
                      alt="Press Peels Upload" 
                      style={{ 
                        width: isSmallMobile ? '150px' : isStandardMobile ? '165px' : isMobile ? '180px' : '200px', 
                        height: 'auto',
                        maxWidth: '100%',
                        objectFit: 'contain',
                        filter: 'brightness(1.2) drop-shadow(0 2px 8px rgba(16, 185, 129, 0.3))'
                      }} 
                    />
                    <svg width={isSmallMobile ? "20" : isStandardMobile ? "22" : "24"} height={isSmallMobile ? "20" : isStandardMobile ? "22" : "24"} viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7,10 12,15 17,10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                  </div>
                ) : (
                  <img 
                    src="/press-peels-logo-03.svg" 
                    alt="Press Peels Upload" 
                    style={{ 
                      width: isSmallMobile ? '150px' : isStandardMobile ? '165px' : isMobile ? '180px' : '200px', 
                      height: 'auto',
                      maxWidth: '100%',
                      objectFit: 'contain',
                      opacity: 0.7,
                      transition: 'all 0.2s ease'
                    }} 
                  />
                )}
              </div>
              <div style={{ 
                fontSize: isSmallMobile ? '0.875rem' : isStandardMobile ? '0.9rem' : isMobile ? '0.95rem' : '1rem', 
                fontWeight: '500', 
                color: isDragging ? '#10b981' : '#374151',
                marginBottom: '0.5rem',
                lineHeight: '1.4'
              }}>
                {isDragging 
                  ? 'Drop your image here!' 
                  : selectedFile 
                    ? 'Change Image' 
                    : 'Click to upload or drag & drop'
                }
              </div>
              <div style={{ 
                fontSize: isSmallMobile ? '0.75rem' : isStandardMobile ? '0.775rem' : isMobile ? '0.8rem' : '0.9rem', 
                color: isDragging ? '#059669' : '#6b7280',
                lineHeight: '1.4'
              }}>
                PNG files, up to 100MB
              </div>
              <div style={{ 
                fontSize: isSmallMobile ? '0.75rem' : isStandardMobile ? '0.8rem' : isMobile ? '0.85rem' : '0.9rem', 
                color: '#059669',
                lineHeight: '1.5',
                marginTop: '0.5rem',
                fontWeight: '500',
                maxWidth: '100%',
                textAlign: 'center'
              }}>
                💡 For best results, use 300 DPI images
              </div>
              <input
                id="image-upload"
                ref={fileInputRef}
                type="file"
                accept="image/png"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </label>
          </div>

          {/* Image Preview */}
          {selectedFile && (
            <div style={{ marginBottom: isSmallMobile ? '1.5rem' : isStandardMobile ? '1.75rem' : '2rem' }}>
              <h3 style={{ 
                fontSize: isSmallMobile ? '1.1rem' : isStandardMobile ? '1.15rem' : isMobile ? '1.2rem' : '1.3rem', 
                fontWeight: '600', 
                marginBottom: '1rem', 
                color: '#111827',
                lineHeight: '1.3'
              }}>
                Image Preview
              </h3>
              <div style={{ 
                display: 'flex', 
                gap: isSmallMobile ? '0.75rem' : isStandardMobile ? '0.875rem' : isMobile ? '1rem' : '2rem', 
                alignItems: 'flex-start',
                flexDirection: isSmallScreen ? 'column' : 'row',
                flexWrap: 'wrap'
              }}>
                <img
                  src={URL.createObjectURL(selectedFile)}
                  alt="Preview"
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: isSmallMobile ? '200px' : isStandardMobile ? '225px' : isMobile ? '250px' : '300px', 
                    width: isSmallScreen ? '100%' : 'auto',
                    height: 'auto',
                    border: '1px solid #e5e7eb',
                    objectFit: 'contain'
                  }}
                />
                <div style={{ flex: 1, minWidth: isSmallScreen ? 'auto' : '250px' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ 
                      color: '#374151',
                      fontSize: isSmallMobile ? '0.875rem' : isStandardMobile ? '0.9rem' : '1rem'
                    }}>Image Dimensions:</strong>
                    <div style={{ 
                      color: '#6b7280', 
                      marginTop: '0.25rem',
                      fontSize: isSmallMobile ? '0.8rem' : isStandardMobile ? '0.85rem' : '0.9rem'
                    }}>
                      {imgWidth} × {imgHeight} pixels
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}


          {/* Manual DPI Input */}
          {showDPIInput && selectedFile && (
            <div style={{ 
              background: '#f0f9ff', 
              border: '1px solid #0ea5e9', 
              borderRadius: '8px', 
              padding: '1rem', 
              marginBottom: '1rem' 
            }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#0c4a6e' }}>DPI Not Detected</h4>
              <p style={{ margin: '0 0 1rem 0', color: '#0369a1', fontSize: '0.9rem' }}>
                Please specify the DPI of your image (72-600):
              </p>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="number"
                  value={manualDPI}
                  onChange={handleManualDPIChange}
                  min="72"
                  max="600"
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #0ea5e9',
                    borderRadius: '4px',
                    width: '80px'
                  }}
                />
                <span style={{ color: '#0369a1' }}>DPI</span>
                <button
                  onClick={() => {
                    setShowDPIInput(false);
                    if (imgWidth && imgHeight) {
                      const validation = validateImage(imgWidth, imgHeight, manualDPI, selectedFile.type);
                      if (validation.valid) {
                        const closest = findClosestVariant(validation.widthIn, validation.heightIn, variants);
                        setSelectedVariant(closest);
                      }
                    }
                  }}
                  style={{
                    background: '#0ea5e9',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - 5 Boxes Grid */}
        <div style={{
          display: 'grid',
          gridTemplateRows: 'auto auto auto auto auto',
          height: 'auto',
          gap: '1rem'
        }}>

            {/* Box 1 - Pricing Section */}
            <div style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderRadius: '12px',
              padding: '1.5rem',
              border: '1px solid #e2e8f0'
            }}>
              {cartItems.length === 0 ? (
                // No items in cart - show simple item pricing
                <>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1rem', color: '#64748b', fontWeight: '500' }}>Item Pricing</span>
                  </div>
                  {selectedVariant && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.95rem', color: '#374151', marginBottom: '0.25rem' }}>
                        {printWidth && printHeight 
                          ? `${printWidth.toFixed(2)}" × ${printHeight.toFixed(2)}" Press Peels Print`
                          : 'Press Peels Print'
                        }
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                        {printWidth && printHeight ? (
                          <>
                            Qty: {quantity} × ${(calculateVolumePricing(printWidth, printHeight, quantity).totalPrice / quantity).toFixed(2)} = 
                            <span style={{ 
                              fontSize: '1.5rem', 
                              fontWeight: '700', 
                              color: '#111827',
                              marginLeft: '0.5rem'
                            }}>
                              ${calculateVolumePricing(printWidth, printHeight, quantity).totalPrice.toFixed(2)}
                            </span>
                          </>
                        ) : (
                          <span style={{ color: '#6b7280', fontStyle: 'italic' }}>
                            Upload an image to see pricing
                          </span>
                        )}
                      </div>
                      
                      {/* Rate and Package Weight */}
                      {printWidth && printHeight && (
                        <div style={{
                          marginTop: '1rem',
                          paddingTop: '1rem',
                          borderTop: '1px solid #e2e8f0',
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                          gap: '1rem'
                        }}>
                          <div>
                            <span style={{ color: '#6b7280', fontWeight: '500', fontSize: '0.9rem' }}>Rate:</span>
                            <div style={{ color: '#111827', fontWeight: '600', fontSize: '0.9rem' }}>
                              ${(() => {
                                // Calculate total square inches for all items (cart + current item)
                                const cartTotalSqIn = cartItems.reduce((total, item) => {
                                  if (item.customData?.printWidth && item.customData?.printHeight) {
                                    return total + (item.customData.printWidth * item.customData.printHeight * item.quantity);
                                  }
                                  return total;
                                }, 0);
                                const currentItemSqIn = printWidth && printHeight ? (printWidth * printHeight * quantity) : 0;
                                const totalSqIn = cartTotalSqIn + currentItemSqIn;
                                
                                // Get the tier rate based on total square inches
                                let tierRate = 0;
                                if (totalSqIn > 0) {
                                  if (totalSqIn >= 0 && totalSqIn <= 10) {
                                    tierRate = 0.35;
                                  } else if (totalSqIn >= 11 && totalSqIn <= 20) {
                                    tierRate = 0.25;
                                  } else if (totalSqIn >= 21 && totalSqIn <= 50) {
                                    tierRate = 0.15;
                                  } else if (totalSqIn >= 51 && totalSqIn <= 100) {
                                    tierRate = 0.1;
                                  } else if (totalSqIn >= 101 && totalSqIn <= 200) {
                                    tierRate = 0.08;
                                  } else if (totalSqIn >= 201 && totalSqIn <= 400) {
                                    tierRate = 0.07;
                                  } else if (totalSqIn >= 401 && totalSqIn <= 600) {
                                    tierRate = 0.06;
                                  } else if (totalSqIn >= 601 && totalSqIn <= 800) {
                                    tierRate = 0.05;
                                  } else if (totalSqIn >= 801 && totalSqIn <= 1000) {
                                    tierRate = 0.04;
                                  } else if (totalSqIn >= 1001 && totalSqIn <= 1500) {
                                    tierRate = 0.03;
                                  } else if (totalSqIn >= 1501 && totalSqIn <= 2500) {
                                    tierRate = 0.025;
                                  } else if (totalSqIn >= 2501) {
                                    tierRate = 0.02;
                                  }
                                }
                                return tierRate.toFixed(3);
                              })()} per sq in
                            </div>
                          </div>
                          <div>
                            <span style={{ color: '#6b7280', fontWeight: '500', fontSize: '0.9rem' }}>Package Weight:</span>
                            <div style={{ color: '#111827', fontWeight: '600', fontSize: '0.9rem' }}>
                              {(() => {
                                // Calculate total square inches for all items (cart + current item)
                                const cartTotalSqIn = cartItems.reduce((total, item) => {
                                  if (item.customData?.printWidth && item.customData?.printHeight) {
                                    return total + (item.customData.printWidth * item.customData.printHeight * item.quantity);
                                  }
                                  return total;
                                }, 0);
                                const currentItemSqIn = printWidth && printHeight ? (printWidth * printHeight * quantity) : 0;
                                const totalSqIn = cartTotalSqIn + currentItemSqIn;
                                return calculateDynamicWeight(totalSqIn).toFixed(3);
                              })()} lbs
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {loadingVariants && (
                    <div style={{ 
                      fontSize: '1rem', 
                      color: '#64748b',
                      fontStyle: 'italic',
                      textAlign: 'center'
                    }}>
                      Loading pricing...
                    </div>
                  )}
                </>
              ) : (
                // Has items in cart - show comprehensive order summary
                <>
                  <div style={{ marginBottom: '1rem' }}>
                    <span style={{ fontSize: '1.1rem', color: '#374151', fontWeight: '600' }}>Order Summary</span>
                  </div>
                  
                  {/* Current Item */}
                  {selectedVariant && (
                    <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                        <span style={{ fontSize: '0.9rem', color: '#6b7280', fontWeight: '500' }}>This Item:</span>
                        <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#374151' }}>
                          {printWidth && printHeight ? (
                            `$${calculateVolumePricing(printWidth, printHeight, quantity).totalPrice.toFixed(2)}`
                          ) : (
                            <span style={{ color: '#6b7280', fontSize: '0.9rem', fontStyle: 'italic' }}>
                              Processing...
                            </span>
                          )}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
                        {printWidth && printHeight 
                          ? `${printWidth.toFixed(2)}" × ${printHeight.toFixed(2)}" × ${quantity}`
                          : `Qty: ${quantity}`
                        }
                      </div>
                    </div>
                  )}
                  
                  {/* Cart Items */}
                  <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                      <span style={{ fontSize: '0.9rem', color: '#6b7280', fontWeight: '500' }}>
                        Cart ({cartItems.reduce((total, item) => total + item.quantity, 0)} print{cartItems.reduce((total, item) => total + item.quantity, 0) !== 1 ? 's' : ''}):
                      </span>
                      <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#374151' }}>
                        ${(() => {
                          // Calculate volume pricing for cart items only
                          const cartTotalSqIn = cartItems.reduce((total, item) => {
                            if (item.customData?.printWidth && item.customData?.printHeight) {
                              return total + (item.customData.printWidth * item.customData.printHeight * item.quantity);
                            }
                            return total;
                          }, 0);
                          
                          if (cartTotalSqIn > 0) {
                            let tierRate = 0;
                            if (cartTotalSqIn >= 0 && cartTotalSqIn <= 10) {
                              tierRate = 0.35;
                            } else if (cartTotalSqIn >= 11 && cartTotalSqIn <= 20) {
                              tierRate = 0.25;
                            } else if (cartTotalSqIn >= 21 && cartTotalSqIn <= 50) {
                              tierRate = 0.15;
                            } else if (cartTotalSqIn >= 51 && cartTotalSqIn <= 100) {
                              tierRate = 0.1;
                            } else if (cartTotalSqIn >= 101 && cartTotalSqIn <= 200) {
                              tierRate = 0.08;
                            } else if (cartTotalSqIn >= 201 && cartTotalSqIn <= 400) {
                              tierRate = 0.07;
                            } else if (cartTotalSqIn >= 401 && cartTotalSqIn <= 600) {
                              tierRate = 0.06;
                            } else if (cartTotalSqIn >= 601 && cartTotalSqIn <= 800) {
                              tierRate = 0.05;
                            } else if (cartTotalSqIn >= 801 && cartTotalSqIn <= 1000) {
                              tierRate = 0.04;
                            } else if (cartTotalSqIn >= 1001 && cartTotalSqIn <= 1500) {
                              tierRate = 0.03;
                            } else if (cartTotalSqIn >= 1501 && cartTotalSqIn <= 2500) {
                              tierRate = 0.025;
                            } else if (cartTotalSqIn >= 2501) {
                              tierRate = 0.02;
                            }
                            return (cartTotalSqIn * tierRate).toFixed(2);
                          }
                          return '0.00';
                        })()}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
                      Total Square Inches: {(() => {
                        const cartTotalSqIn = cartItems.reduce((total, item) => {
                          if (item.customData?.printWidth && item.customData?.printHeight) {
                            return total + (item.customData.printWidth * item.customData.printHeight * item.quantity);
                          }
                          return total;
                        }, 0);
                        const currentItemSqIn = printWidth && printHeight ? (printWidth * printHeight * quantity) : 0;
                        return Math.ceil(cartTotalSqIn + currentItemSqIn);
                      })()}
                    </div>
                  </div>
                  
                  {/* Rate and Package Weight */}
                  <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.9rem', color: '#6b7280', fontWeight: '500' }}>Rate:</span>
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
                        ${(() => {
                          // If there's a current item being configured, calculate rate with it included
                          const hasCurrentItem = printWidth && printHeight && selectedFile;
                          
                          if (hasCurrentItem) {
                            const cartTotalSqIn = cartItems.reduce((total, item) => {
                              if (item.customData?.printWidth && item.customData?.printHeight) {
                                return total + (item.customData.printWidth * item.customData.printHeight * item.quantity);
                              }
                              return total;
                            }, 0);
                            const currentItemSqIn = printWidth * printHeight * quantity;
                            const totalSqIn = cartTotalSqIn + currentItemSqIn;
                            
                            // Use the SAME volume pricing logic as repriceCartItems function
                            let volumeRate = 0;
                            if (totalSqIn >= 0 && totalSqIn <= 10) {
                              volumeRate = 0.35;
                            } else if (totalSqIn >= 11 && totalSqIn <= 20) {
                              volumeRate = 0.25;
                            } else if (totalSqIn >= 21 && totalSqIn <= 50) {
                              volumeRate = 0.15;
                            } else if (totalSqIn >= 51 && totalSqIn <= 100) {
                              volumeRate = 0.1;
                            } else if (totalSqIn >= 101 && totalSqIn <= 200) {
                              volumeRate = 0.08;
                            } else if (totalSqIn >= 201 && totalSqIn <= 400) {
                              volumeRate = 0.07;
                            } else if (totalSqIn >= 401 && totalSqIn <= 600) {
                              volumeRate = 0.06;
                            } else if (totalSqIn >= 601 && totalSqIn <= 800) {
                              volumeRate = 0.05;
                            } else if (totalSqIn >= 801 && totalSqIn <= 1000) {
                              volumeRate = 0.04;
                            } else if (totalSqIn >= 1001 && totalSqIn <= 1500) {
                              volumeRate = 0.03;
                            } else if (totalSqIn >= 1501 && totalSqIn <= 2500) {
                              volumeRate = 0.025;
                            } else if (totalSqIn >= 2501) {
                              volumeRate = 0.02;
                            }
                            return volumeRate.toFixed(3);
                          } else {
                            // Just cart items - calculate rate from cart total
                            const cartTotalSqIn = cartItems.reduce((total, item) => {
                              if (item.customData?.printWidth && item.customData?.printHeight) {
                                return total + (item.customData.printWidth * item.customData.printHeight * item.quantity);
                              }
                              return total;
                            }, 0);
                            
                            if (cartTotalSqIn > 0) {
                              let volumeRate = 0;
                              if (cartTotalSqIn >= 0 && cartTotalSqIn <= 10) {
                                volumeRate = 0.35;
                              } else if (cartTotalSqIn >= 11 && cartTotalSqIn <= 20) {
                                volumeRate = 0.25;
                              } else if (cartTotalSqIn >= 21 && cartTotalSqIn <= 50) {
                                volumeRate = 0.15;
                              } else if (cartTotalSqIn >= 51 && cartTotalSqIn <= 100) {
                                volumeRate = 0.1;
                              } else if (cartTotalSqIn >= 101 && cartTotalSqIn <= 200) {
                                volumeRate = 0.08;
                              } else if (cartTotalSqIn >= 201 && cartTotalSqIn <= 400) {
                                volumeRate = 0.07;
                              } else if (cartTotalSqIn >= 401 && cartTotalSqIn <= 600) {
                                volumeRate = 0.06;
                              } else if (cartTotalSqIn >= 601 && cartTotalSqIn <= 800) {
                                volumeRate = 0.05;
                              } else if (cartTotalSqIn >= 801 && cartTotalSqIn <= 1000) {
                                volumeRate = 0.04;
                              } else if (cartTotalSqIn >= 1001 && cartTotalSqIn <= 1500) {
                                volumeRate = 0.03;
                              } else if (cartTotalSqIn >= 1501 && cartTotalSqIn <= 2500) {
                                volumeRate = 0.025;
                              } else if (cartTotalSqIn >= 2501) {
                                volumeRate = 0.02;
                              }
                              return volumeRate.toFixed(3);
                            }
                            return '0.000';
                          }
                        })()} per sq in
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.9rem', color: '#6b7280', fontWeight: '500' }}>Package Weight:</span>
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
                        {(() => {
                          const cartTotalSqIn = cartItems.reduce((total, item) => {
                            if (item.customData?.printWidth && item.customData?.printHeight) {
                              return total + (item.customData.printWidth * item.customData.printHeight * item.quantity);
                            }
                            return total;
                          }, 0);
                          const currentItemSqIn = printWidth && printHeight ? (printWidth * printHeight * quantity) : 0;
                          const totalSqIn = cartTotalSqIn + currentItemSqIn;
                          return calculateDynamicWeight(totalSqIn).toFixed(3);
                        })()} lbs
                      </span>
                    </div>
                  </div>

                  {/* Order Total */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '1.1rem', color: '#111827', fontWeight: '600' }}>Order Total:</span>
                    <span style={{ 
                      fontSize: '1.75rem', 
                      fontWeight: '700', 
                      color: '#059669',
                      letterSpacing: '-0.025em'
                    }}>
                      ${(() => {
                        // If there's a current item being configured, calculate total with it included
                        const hasCurrentItem = printWidth && printHeight && selectedFile;
                        
                        if (hasCurrentItem) {
                          // Calculate total square inches for all items (cart + current item)
                          const cartTotalSqIn = cartItems.reduce((total, item) => {
                            if (item.customData?.printWidth && item.customData?.printHeight) {
                              return total + (item.customData.printWidth * item.customData.printHeight * item.quantity);
                            }
                            return total;
                          }, 0);
                          const currentItemSqIn = printWidth * printHeight * quantity;
                          const totalSqIn = cartTotalSqIn + currentItemSqIn;
                          
                          // Use the SAME volume pricing logic as repriceCartItems function
                          let volumeRate = 0;
                          if (totalSqIn >= 0 && totalSqIn <= 10) {
                            volumeRate = 0.35;
                          } else if (totalSqIn >= 11 && totalSqIn <= 20) {
                            volumeRate = 0.25;
                          } else if (totalSqIn >= 21 && totalSqIn <= 50) {
                            volumeRate = 0.15;
                          } else if (totalSqIn >= 51 && totalSqIn <= 100) {
                            volumeRate = 0.1;
                          } else if (totalSqIn >= 101 && totalSqIn <= 200) {
                            volumeRate = 0.08;
                          } else if (totalSqIn >= 201 && totalSqIn <= 400) {
                            volumeRate = 0.07;
                          } else if (totalSqIn >= 401 && totalSqIn <= 600) {
                            volumeRate = 0.06;
                          } else if (totalSqIn >= 601 && totalSqIn <= 800) {
                            volumeRate = 0.05;
                          } else if (totalSqIn >= 801 && totalSqIn <= 1000) {
                            volumeRate = 0.04;
                          } else if (totalSqIn >= 1001 && totalSqIn <= 1500) {
                            volumeRate = 0.03;
                          } else if (totalSqIn >= 1501 && totalSqIn <= 2500) {
                            volumeRate = 0.025;
                          } else if (totalSqIn >= 2501) {
                            volumeRate = 0.02;
                          }
                          return (totalSqIn * volumeRate).toFixed(2);
                        } else {
                          // Just cart items - use their already repriced total
                          const cartTotal = cartItems.reduce((total, item) => total + item.price, 0);
                          return (cartTotal / 100).toFixed(2);
                        }
                      })()}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Box 2 - Image Specifications */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              {/* Image Specifications */}
              {imgWidth && imgHeight && (
                <div style={{
                  background: '#f8fafc',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  border: '1px solid #e2e8f0',
                  height: 'fit-content'
                }}>
                  <h3 style={{ 
                    margin: '0 0 1rem 0', 
                    fontSize: '1.1rem', 
                    fontWeight: '600', 
                    color: '#374151'
                  }}>
                    Print Specifications
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', fontSize: '0.95rem' }}>
                    <div>
                      <span style={{ color: '#6b7280', fontWeight: '500' }}>Image Size:</span>
                      <div style={{ color: '#111827', fontWeight: '600' }}>{imgWidth} × {imgHeight} px</div>
                    </div>
                    <div>
                      <span style={{ color: '#6b7280', fontWeight: '500' }}>Print Size:</span>
                      <div style={{ color: '#111827', fontWeight: '600' }}>
                        {printWidth && printHeight 
                          ? `${printWidth.toFixed(2)}" × ${printHeight.toFixed(2)}"`
                          : 'Calculating...'
                        }
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6b7280', fontWeight: '500' }}>Image DPI:</span>
                      <div style={{ color: '#111827', fontWeight: '600' }}>
                        {detectedDPI ? `${detectedDPI} DPI` : 'Detecting...'}
                      </div>
                    </div>
                    {printWidth && printHeight && (
                      <>
                        <div>
                          <span style={{ color: '#6b7280', fontWeight: '500' }}>Total Square Inches:</span>
                          <div style={{ color: '#111827', fontWeight: '600' }}>
                            {quantity === 1 
                              ? `${Math.ceil(calculateVolumePricing(printWidth, printHeight, quantity).perPrintSquareInches)} sq in`
                              : `${Math.ceil(calculateVolumePricing(printWidth, printHeight, quantity).perPrintSquareInches)} × ${quantity} = ${Math.ceil(calculateVolumePricing(printWidth, printHeight, quantity).totalSquareInches)} sq in`
                            }
                          </div>
                        </div>

                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Box 3 - Quantity Controls & Add to Cart (Always Visible) */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              {/* Quantity Selector */}
              {selectedFile && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: isSmallScreen ? 'stretch' : 'center', 
                  gap: isSmallMobile ? '0.75rem' : '1rem', 
                  flexDirection: isSmallScreen ? 'column' : 'row' 
                }}>
                  <span style={{ 
                    fontSize: isSmallMobile ? '0.95rem' : '1rem', 
                    fontWeight: '600', 
                    color: '#374151', 
                    textAlign: isSmallScreen ? 'center' : 'left' 
                  }}>Quantity:</span>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '10px', 
                    justifyContent: 'center', 
                    minHeight: '48px',
                    background: '#ffffff'
                  }}>
                    <button
                      onClick={() => {
                        const newQuantity = Math.max(1, quantity - 1);
                        setQuantity(newQuantity);
                        setQuantityInput(newQuantity.toString());
                        
                        // Track price calculation in Google Analytics
                        if (printWidth && printHeight) {
                          const pricing = calculateVolumePricing(printWidth, printHeight, newQuantity);
                          trackPriceCalculation(newQuantity, pricing.totalPrice);
                        }
                      }}
                      style={{
                        background: 'transparent', 
                        border: 'none', 
                        padding: isSmallMobile ? '0.75rem 0.875rem' : '0.8rem 1rem', 
                        fontSize: '1.2rem', 
                        cursor: 'pointer', 
                        color: '#6b7280',
                        fontWeight: '600',
                        borderRadius: '10px 0 0 10px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background = '#f3f4f6';
                        e.target.style.color = '#374151';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = 'transparent';
                        e.target.style.color = '#6b7280';
                      }}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      value={quantityInput}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        setQuantityInput(inputValue);
                        
                        // Update quantity in real-time if valid number, but allow empty field
                        if (inputValue !== '') {
                          const numValue = parseInt(inputValue);
                          if (!isNaN(numValue) && numValue >= 1 && numValue <= 999) {
                            setQuantity(numValue);
                          }
                        }
                      }}
                      onBlur={(e) => {
                        const value = parseInt(e.target.value) || 1;
                        const finalQuantity = Math.max(1, Math.min(999, value));
                        setQuantity(finalQuantity);
                        setQuantityInput(finalQuantity.toString());
                        
                        // Track price calculation in Google Analytics
                        if (printWidth && printHeight) {
                          const pricing = calculateVolumePricing(printWidth, printHeight, finalQuantity);
                          trackPriceCalculation(finalQuantity, pricing.totalPrice);
                        }
                      }}
                      min="1"
                      max="999"
                      style={{ 
                        padding: isSmallMobile ? '0.75rem 1rem' : '0.8rem 1.2rem', 
                        fontSize: isSmallMobile ? '1rem' : '1.1rem', 
                        fontWeight: '600', 
                        color: '#111827',
                        backgroundColor: '#f9fafb',
                        borderTop: '1px solid #e2e8f0',
                        borderBottom: '1px solid #e2e8f0',
                        borderLeft: 'none',
                        borderRight: 'none',
                        minWidth: '60px',
                        textAlign: 'center',
                        outline: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'textfield'
                      }}
                    />
                    <button
                      onClick={() => {
                        const newQuantity = Math.min(999, quantity + 1);
                        setQuantity(newQuantity);
                        setQuantityInput(newQuantity.toString());
                        
                        // Track price calculation in Google Analytics
                        if (printWidth && printHeight) {
                          const pricing = calculateVolumePricing(printWidth, printHeight, newQuantity);
                          trackPriceCalculation(newQuantity, pricing.totalPrice);
                        }
                      }}
                      style={{
                        background: 'transparent', 
                        border: 'none', 
                        padding: isSmallMobile ? '0.75rem 0.875rem' : '0.8rem 1rem', 
                        fontSize: '1.2rem', 
                        cursor: 'pointer', 
                        color: '#6b7280',
                        fontWeight: '600',
                        borderRadius: '0 10px 10px 0',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background = '#f3f4f6';
                        e.target.style.color = '#374151';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = 'transparent';
                        e.target.style.color = '#6b7280';
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
              )}




            </div>

            {/* Box 4 - Error/Success Messages */}
            <div>
              {error && (
                <div style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  color: '#dc2626',
                  padding: '1rem',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  // Longer messages are written as paragraphs; without this the
                  // line breaks collapse into one dense block. Single-line
                  // messages are unaffected.
                  whiteSpace: 'pre-line',
                  lineHeight: '1.5'
                }}>
                  {error}
                </div>
              )}
              {successMessage && (
                <div style={{
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  color: '#16a34a',
                  padding: '1rem',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: '500'
                }}>
                  {successMessage}
                </div>
              )}
            </div>

            {/* Box 5 - Checkout Button */}
            <div>
              {cartItems.length > 0 || selectedVariant ? (
                <button
                  onClick={async () => {
                    // Synchronous guard: block a second click before the loadingCart
                    // state update has propagated through React's render cycle.
                    if (checkoutInProgress.current) return;
                    checkoutInProgress.current = true;
                    try {
                      if (isBiz) {
                        if (cartItems.length > 0) setShowBizForm(true);
                      } else {
                        if (cartItems.length > 0) {
                          await handleMultiItemCheckout(cartItems);
                        } else if (selectedVariant && selectedFile) {
                          await handleStripeCheckout();
                        }
                      }
                    } finally {
                      checkoutInProgress.current = false;
                    }
                  }}
                  style={{ 
                    width: '100%',
                    background: (loadingCart || (cartItems.length === 0 && (!printWidth || !printHeight))) ? '#9ca3af' : 'linear-gradient(135deg, #635bff 0%, #5a52e8 100%)',
                    color: '#ffffff', 
                    fontWeight: '600', 
                    fontSize: isMobile ? '1rem' : '1.1rem', 
                    border: 'none', 
                    borderRadius: '10px', 
                    padding: isMobile ? '1rem 1.5rem' : '1rem 2rem',
                    minHeight: '48px',
                    cursor: (loadingCart || (cartItems.length === 0 && (!printWidth || !printHeight))) ? 'not-allowed' : 'pointer',
                    opacity: (loadingCart || (cartItems.length === 0 && (!printWidth || !printHeight))) ? 0.7 : 1,
                    transition: 'all 0.2s ease',
                    boxShadow: (loadingCart || (cartItems.length === 0 && (!printWidth || !printHeight))) ? 'none' : '0 4px 15px rgba(99, 91, 255, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                  disabled={loadingCart || (cartItems.length === 0 && (!printWidth || !printHeight))}
                  onMouseOver={(e) => {
                    if (!loadingCart && !(cartItems.length === 0 && (!printWidth || !printHeight))) {
                      e.target.style.background = 'linear-gradient(135deg, #5a52e8 0%, #4c46d6 100%)';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(99, 91, 255, 0.4)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!loadingCart && !(cartItems.length === 0 && (!printWidth || !printHeight))) {
                      e.target.style.background = 'linear-gradient(135deg, #635bff 0%, #5a52e8 100%)';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 15px rgba(99, 91, 255, 0.3)';
                    }
                  }}
                >
                  {isBiz ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 19-7z" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.90-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                    </svg>
                  )}
                  {mainCheckoutLabel}
                </button>
              ) : (
                <button
                  style={{ 
                    width: '100%',
                    background: '#f3f4f6',
                    color: '#6b7280', 
                    fontWeight: '600', 
                    fontSize: isMobile ? '1rem' : '1.1rem', 
                    border: 'none', 
                    borderRadius: '10px', 
                    padding: isMobile ? '1rem 1.5rem' : '1rem 2rem',
                    minHeight: '48px',
                    cursor: 'not-allowed'
                  }}
                  disabled
                >
                  Upload Image First
                </button>
              )}
            </div>

          </div>
        </div>
        </div>
      </div>
      
      {/* Shopping Cart Preview - Separate container below main interface */}
      <div ref={cartContainerRef} id="cart-container-anchor" />
      <ShoppingCartPreview
        cartItems={cartItems}
        removeCartItem={removeCartItem}
        updateCartItem={updateCartItem}
        onCheckout={isBiz ? (items) => { if (items.length > 0) setShowBizForm(true); } : handleMultiItemCheckout}
        isBiz={isBiz}
        // Cart item resize props
        onCartItemResize={handleCartItemResize}
        CartItemResizer={CartItemResizer}
        API_BASE_URL={API_BASE_URL}
        setIsResizing={setIsResizing}
        // Remove white/black background handlers
        onRemoveWhiteBackground={handleRemoveWhiteBackground}
        onRemoveBlackBackground={handleRemoveBlackBackground}
        // New props for buttons
        fileInputRef={fileInputRef}
        loadingCart={loadingCart}
        // Drag and drop props for cart
        isCartDragging={isCartDragging}
        onCartDragEnter={handleCartDragEnter}
        onCartDragLeave={handleCartDragLeave}
        onCartDragOver={handleCartDragOver}
        onCartDrop={handleCartDrop}
      />
      
      {/* Footer */}
      <Footer />
      

    </div>
  );
} 